var __require = /* @__PURE__ */ ((x) => typeof require !== "undefined" ? require : typeof Proxy !== "undefined" ? new Proxy(x, {
  get: (a, b) => (typeof require !== "undefined" ? require : a)[b]
}) : x)(function(x) {
  if (typeof require !== "undefined") return require.apply(this, arguments);
  throw Error('Dynamic require of "' + x + '" is not supported');
});

// src/testUtils.tsx
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as testLib from "@testing-library/react";
import { act } from "react";
import { Provider } from "react-redux";

// src/slices/accounts/accounts.ts
import { createSlice } from "@reduxjs/toolkit";
import {
  isChainFullId,
  isDeprecated as isDeprecated2
} from "@umami/chains";
import { CustomError as CustomError3 } from "@umami/utils";
import { capitalize, remove } from "lodash";

// src/thunks/changeMnemonicPassword.ts
import { createAsyncThunk } from "@reduxjs/toolkit";
import { CustomError as CustomError2 } from "@umami/utils";

// src/utils/encryption.ts
import { decrypt, encrypt } from "@umami/crypto";
import { CustomError } from "@umami/utils";
import { fromPairs } from "lodash";
var transformKeyValuePairs = async ({
  password,
  pairs = {},
  action
}) => fromPairs(
  await Promise.all(
    Object.entries(pairs).map(async ([fingerprint, currentValue]) => {
      try {
        if (!currentValue) {
          throw new CustomError("No value found");
        }
        const value = action === "encrypt" ? await encrypt(currentValue, password) : await decrypt(currentValue, password);
        return [fingerprint, value];
      } catch (err) {
        throw new CustomError(err.message);
      }
    })
  )
);
var decryptKeyValuePairs = async ({
  password,
  pairs
}) => transformKeyValuePairs({ password, pairs, action: "decrypt" });
var encryptKeyValuePairs = async ({
  password,
  pairs
}) => transformKeyValuePairs({ password, pairs, action: "encrypt" });

// src/thunks/changeMnemonicPassword.ts
var changeMnemonicPassword = createAsyncThunk("accounts/changeMnemonicPassword", async ({ currentPassword, newPassword }, { getState }) => {
  if (currentPassword === newPassword) {
    throw new CustomError2("New password must be different from the current password");
  }
  const { items: accounts, seedPhrases } = getState().accounts;
  if (accounts.filter((account) => account.type === "mnemonic").length === 0) {
    throw new CustomError2("No mnemonic accounts found");
  }
  const decryptedMnemonics = await decryptKeyValuePairs({
    password: currentPassword,
    pairs: seedPhrases
  });
  const newEncryptedMnemonics = await encryptKeyValuePairs({
    password: newPassword,
    pairs: decryptedMnemonics
  });
  return { newEncryptedMnemonics };
});

// src/utils/deprecatedChains.ts
import { MAINNET, getChainFullIdFromNetwork, isDeprecated } from "@umami/chains";
var chainFullIdOf = (network) => {
  if (!network) {
    return void 0;
  }
  if (network.namespace && network.identifier) {
    return getChainFullIdFromNetwork(network);
  }
  return network.chainId;
};
var isDeprecatedNetwork = (network) => {
  const chainFullId = chainFullIdOf(network);
  return !!chainFullId && isDeprecated(chainFullId);
};
var withoutDeprecatedNetworks = (state) => {
  const draft = state;
  if (!draft || typeof draft !== "object") {
    return state;
  }
  const result = { ...draft };
  if (result.configs && typeof result.configs === "object") {
    result.configs = Object.fromEntries(
      Object.entries(result.configs).filter(([chainFullId]) => !isDeprecated(chainFullId))
    );
  }
  if (Array.isArray(result.available)) {
    result.available = result.available.filter((network) => !isDeprecatedNetwork(network));
  }
  if (result.networks && typeof result.networks === "object") {
    result.networks = Object.fromEntries(
      Object.entries(result.networks).map(([chain, tracks]) => [
        chain,
        tracks && typeof tracks === "object" ? Object.fromEntries(
          Object.entries(tracks).map(([track, list]) => [
            track,
            Array.isArray(list) ? list.filter((network) => !isDeprecatedNetwork(network)) : list
          ])
        ) : tracks
      ])
    );
  }
  if (isDeprecatedNetwork(result.current)) {
    result.current = result.configs?.[getChainFullIdFromNetwork(MAINNET)] ?? MAINNET;
  }
  return result;
};
var withoutDeprecatedAccountChains = (state) => {
  const draft = state;
  if (!draft || !Array.isArray(draft.items)) {
    return state;
  }
  return {
    ...draft,
    items: draft.items.map((account) => {
      if (!account?.chains) {
        return account;
      }
      return {
        ...account,
        chains: Object.fromEntries(
          Object.entries(account.chains).filter(([chainFullId]) => !isDeprecated(chainFullId))
        )
      };
    })
  };
};

// src/slices/accounts/accounts.ts
var accountsInitialState = {
  items: [],
  seedPhrases: {},
  secretKeys: {},
  password: "",
  prices: {},
  alerts: {
    isSocialLoginWarningShown: false,
    isShieldedXtzFeatureAnnouncementShown: false
  },
  passkeyRotationStatus: "Inactive",
  newEncryptedDek: void 0,
  isPasskeyUser: false
};
var accountsSlice = createSlice({
  name: "accounts",
  initialState: accountsInitialState,
  extraReducers: (builder) => {
    builder.addCase(changeMnemonicPassword.fulfilled, (state, action) => {
      const { newEncryptedMnemonics } = action.payload;
      state.seedPhrases = newEncryptedMnemonics;
    });
  },
  reducers: {
    PURGE: () => void 0,
    reset: () => accountsInitialState,
    // Same as the networks slice: restore bypasses the migration chain, so chain data for
    // retired networks has to be dropped here too.
    restore: (_, { payload }) => withoutDeprecatedAccountChains(payload),
    // Do not call this directly, use useRemoveMnemonic from setAccountDataHooks
    removeMnemonicAndAccounts: (state, { payload }) => {
      const { fingerPrint } = payload;
      const newAccounts = state.items.filter(
        (a) => !(a.type === "mnemonic" && a.seedFingerPrint === fingerPrint)
      );
      state.items = newAccounts;
      delete state.seedPhrases[fingerPrint];
    },
    // Do not call this directly, use useRemoveNonMnemonic from setAccountDataHooks
    removeNonMnemonicAccounts: (state, { payload }) => {
      state.items = remove(
        state.items,
        (account) => account.type === "mnemonic" || account.type !== payload.accountType
      );
      if (payload.accountType === "secret_key") {
        state.secretKeys = {};
      }
    },
    // Do not call this directly, use useRemoveAccount from setAccountDataHooks
    removeAccount: (state, { payload }) => {
      remove(state.items, (account) => account.address.pkh === payload.address.pkh);
      if (payload.type === "secret_key") {
        delete state.secretKeys[payload.address.pkh];
      }
    },
    // Do not call this directly, use the RenameAccount thunk
    renameAccount: (state, { payload }) => {
      const { account, newName } = payload;
      if (newName.length === 0) {
        throw new CustomError3("Cannot rename account to an empty name.");
      }
      if (state.items.find((a) => a.label === newName)) {
        throw new CustomError3(
          `Cannot rename account ${account.address.pkh} to ${newName} since the name already exists.`
        );
      }
      const accountToRename = state.items.find(
        (a) => a.address.pkh === account.address.pkh && a.label === account.label
      );
      if (accountToRename) {
        accountToRename.label = newName;
        if (state.defaultAccount?.address.pkh === accountToRename.address.pkh) {
          state.defaultAccount.label = newName;
        }
      }
    },
    /**
     * For mnemonics used as a part of {@link ./hooks/mnemonic#useDeriveMnemonicAccount} hook (for setting unique account name).
     * */
    addAccount: (state, { payload }) => {
      state.items = concatUnique(state.items, [payload]);
    },
    /**
     * Creates a mnemonic group.
     * Used as a part of {@link ./hooks/mnemonic#useRestoreRevealedMnemonicAccounts} hook (for setting unique account names inside the group).
     * */
    addMnemonicAccounts: (state, {
      payload: { encryptedMnemonic, accounts, seedFingerprint }
    }) => {
      state.items = concatUnique(state.items, accounts);
      state.seedPhrases[seedFingerprint] = encryptedMnemonic;
    },
    addAccountChain: (state, {
      payload
    }) => {
      const account = state.items.find((acc) => acc.id === payload.accountId);
      if (account?.chains) {
        account.chains[payload.chainId] = payload.chainData;
      }
    },
    markChainAsAutoEnabled: (state, { payload }) => {
      const account = state.items.find((acc) => acc.id === payload.accountId);
      if (account) {
        if (!account.autoEnabledChains) {
          account.autoEnabledChains = [payload.chainId];
        } else if (!account.autoEnabledChains.includes(payload.chainId)) {
          account.autoEnabledChains.push(payload.chainId);
        }
        if (state.defaultAccount && state.defaultAccount.id === payload.accountId) {
          if (!state.defaultAccount.autoEnabledChains) {
            state.defaultAccount.autoEnabledChains = [payload.chainId];
          } else if (!state.defaultAccount.autoEnabledChains.includes(payload.chainId)) {
            state.defaultAccount.autoEnabledChains.push(payload.chainId);
          }
        }
      }
    },
    removeAccountChain: (state, { payload }) => {
      const account = state.items.find((account2) => account2.id === payload.accountId);
      if (account?.chains) {
        delete account.chains[payload.chainId];
      }
    },
    addSecretKey: (state, {
      payload: { pkh, encryptedSecretKey }
    }) => {
      state.secretKeys[pkh] = encryptedSecretKey;
    },
    setCurrent: (state, { payload: address }) => {
      state.current = address;
    },
    setDefaultAccount: (state) => {
      if (!state.defaultAccount) {
        state.defaultAccount = hideConfidentialData(state.items[0]);
      }
    },
    setIsVerified: (state, { payload: { pkh, isVerified } }) => {
      const account = state.items.find((account2) => account2.address.pkh === pkh);
      account.isVerified = isVerified;
    },
    setPassword: (state, { payload }) => {
      state.password = payload;
    },
    setAlerts: (state, { payload }) => {
      state.alerts = {
        ...state.alerts,
        [payload.key]: payload.value
      };
    },
    setPrices: (state, { payload }) => {
      state.prices = payload;
    },
    setEncryptedDek: (state, {
      payload: { pkh, encrypted_dek }
    }) => {
      const account = state.items.find((a) => a.address.pkh === pkh);
      if (account && account.encrypted_dek !== void 0) {
        account.encrypted_dek = encrypted_dek;
      }
      if (state.defaultAccount?.address.pkh === pkh && state.defaultAccount.encrypted_dek !== void 0) {
        state.defaultAccount.encrypted_dek = encrypted_dek;
      }
    },
    setPasskeyRotationStatus: (state, { payload: { status } }) => {
      state.passkeyRotationStatus = status;
    },
    setNewEncryptedDek: (state, {
      payload: { rawPkh, encrypted_dek }
    }) => {
      state.newEncryptedDek = { pkh: rawPkh, encrypted_dek };
    },
    clearNewEncryptedDek: (state) => {
      state.newEncryptedDek = void 0;
    },
    setIsPasskeyUser: (state, { payload }) => {
      state.isPasskeyUser = payload;
    }
  }
});
var concatUnique = (existingAccounts, newAccounts) => {
  newAccounts.forEach((newAccount) => {
    let duplicateReason = "";
    const duplicateAccount = existingAccounts.find((existingAccount) => {
      if (!existingAccount.chains || !newAccount.chains) {
        if (existingAccount.address.pkh === newAccount.address.pkh) {
          duplicateReason = `address ${newAccount.address.pkh}`;
          return true;
        }
        return false;
      }
      for (const chainId in newAccount.chains) {
        if (isDeprecated2(chainId)) {
          continue;
        }
        if (!isChainFullId(chainId)) {
          continue;
        }
        const newAccountChainData = newAccount.chains[chainId];
        const existingAccountChainData = existingAccount.chains[chainId];
        const isSameAddress = newAccountChainData && existingAccountChainData && newAccountChainData.address.pkh === existingAccountChainData.address.pkh;
        if (isSameAddress) {
          duplicateReason = `${capitalize(chainId)} chain address ${newAccountChainData.address.pkh}`;
          return true;
        }
      }
      return false;
    });
    if (duplicateAccount) {
      throw new CustomError3(`Can't add account with ${duplicateReason} because it already exists.`);
    }
  });
  return [...existingAccounts, ...newAccounts];
};
var hideConfidentialData = (acct) => {
  const account = { ...acct };
  if (account.type === "social") {
    if (account.label.includes("@")) {
      account.label = account.label.slice(0, 2) + "..." + account.label.slice(account.label.indexOf("@"));
    }
  }
  account.pk = "********";
  account.chains = {};
  return account;
};
var accountsActions = accountsSlice.actions;

// src/slices/announcement.ts
import { createSlice as createSlice2 } from "@reduxjs/toolkit";
var announcementInitialState = { html: "", seen: true };
var announcementSlice = createSlice2({
  name: "announcements",
  initialState: announcementInitialState,
  reducers: {
    reset: () => announcementInitialState,
    setCurrent: (state, { payload }) => {
      if (payload !== state.html) {
        state.seen = false;
      }
      state.html = payload;
    },
    setSeen: (state) => {
      state.seen = true;
    }
  }
});
var announcementActions = announcementSlice.actions;

// src/slices/assets.ts
import { createSlice as createSlice3 } from "@reduxjs/toolkit";
import { fromRawTokenBalance } from "@umami/chains";
import { compact, groupBy, omit, sortBy } from "lodash";
var assetsInitialState = {
  block: {},
  transfers: { tokens: {} },
  accountStates: {},
  bakers: [],
  conversionRate: void 0,
  refetchTrigger: 0,
  isLoading: false,
  lastTimeUpdated: null
};
var assetsSlice = createSlice3({
  name: "assets",
  initialState: assetsInitialState,
  // Reset assets state if accounts are reset
  extraReducers: (builder) => builder.addCase(accountsActions.reset, () => assetsInitialState),
  reducers: {
    reset: () => assetsInitialState,
    updateBlock: (state, { payload }) => {
      state.block = payload;
    },
    updateTokenTransfers: (state, { payload: transfers }) => {
      transfers.forEach((transfer) => {
        state.transfers.tokens[transfer.transactionId] = transfer;
      });
    },
    updateAccountStates: (state, { payload }) => {
      payload.forEach((accountInfo) => {
        const {
          balance: totalBalance,
          address,
          delegate,
          stakedBalance,
          unstakedBalance,
          rollupBonds,
          smartRollupBonds
        } = accountInfo;
        const balance = totalBalance - stakedBalance - unstakedBalance - rollupBonds - smartRollupBonds;
        state.accountStates[address] = {
          ...state.accountStates[address],
          ...{ delegate, balance, stakedBalance }
        };
      });
    },
    updateUnstakeRequests: (state, { payload }) => {
      const groupedByPkh = groupBy(payload, (req) => req.staker.address);
      for (const accountState of Object.values(state.accountStates)) {
        accountState.unstakeRequests = [];
      }
      for (const [pkh, unstakeRequests] of Object.entries(groupedByPkh)) {
        state.accountStates[pkh] = {
          ...state.accountStates[pkh],
          unstakeRequests: sortBy(
            unstakeRequests.map((req) => omit(req, "staker")),
            "cycle"
          )
        };
      }
    },
    // when we change networks current account states become obsolete
    cleanAccountStates: (state) => {
      state.accountStates = {};
    },
    updateTokenBalance: (state, { payload }) => {
      const groupedByPkh = groupBy(payload, (tokenBalance) => tokenBalance.account.address);
      for (const accountState of Object.values(state.accountStates)) {
        accountState.tokens = [];
      }
      for (const [pkh, rawTokenBalances] of Object.entries(groupedByPkh)) {
        const accountTokenBalances = compact(rawTokenBalances.map(fromRawTokenBalance)).map(
          ({ balance, contract, tokenId, lastLevel }) => ({
            balance,
            contract,
            tokenId,
            lastLevel
          })
        );
        state.accountStates[pkh] = {
          ...state.accountStates[pkh],
          tokens: accountTokenBalances
        };
      }
    },
    removeAccountsData: (state, { payload: pkhs }) => {
      pkhs.forEach((pkh) => {
        delete state.accountStates[pkh];
      });
    },
    updateBakers: (state, { payload }) => {
      state.bakers = payload;
    },
    updateConversionRate: (state, { payload: rate }) => {
      state.conversionRate = rate;
    },
    refetch: (state) => {
      state.refetchTrigger += 1;
    },
    setIsLoading: (state, { payload: isLoading }) => {
      state.isLoading = isLoading;
    },
    setLastTimeUpdated: (state, { payload: lastTimeUpdated }) => {
      state.lastTimeUpdated = lastTimeUpdated;
    }
  }
});
var assetsActions = assetsSlice.actions;

// src/slices/batches.ts
import { createSlice as createSlice4 } from "@reduxjs/toolkit";
import {
  DefaultNetworks
} from "@umami/chains";
import { findIndex, fromPairs as fromPairs2 } from "lodash";
var batchesInitialState = fromPairs2(
  DefaultNetworks.map((network) => [network.name, []])
);
var batchesSlice = createSlice4({
  name: "batches",
  initialState: batchesInitialState,
  reducers: {
    restore: (_, { payload }) => payload,
    reset: () => batchesInitialState,
    // Don't use this action directly. Use thunk estimateAndUpdateBatch
    // @ts-ignore: TS2589 Type instantiation is excessively deep and possibly infinite
    add: (state, {
      payload: { operations, network }
    }) => {
      if (!(network.name in state)) {
        state[network.name] = [];
      }
      const batches = state[network.name];
      const existing = batches.find(
        (batch) => batch.sender.address.pkh === operations.sender.address.pkh
      );
      if (existing) {
        existing.operations.push(...operations.operations);
        return;
      }
      batches.push(operations);
    },
    clear: (state, { payload: { pkh, network } }) => {
      const batches = state[network.name] || [];
      const index = findIndex(batches, (batch) => batch.sender.address.pkh === pkh);
      if (index === -1) {
        return;
      }
      batches.splice(index, 1);
    },
    removeItem: (state, {
      payload: { pkh, index, network }
    }) => {
      const batches = state[network.name] || [];
      const batchIndex = findIndex(batches, (batch) => batch.sender.address.pkh === pkh);
      if (batchIndex === -1) {
        return;
      }
      const existingBatch = batches[batchIndex];
      if (index < existingBatch.operations.length) {
        existingBatch.operations.splice(index, 1);
      }
      if (existingBatch.operations.length === 0) {
        batches.splice(batchIndex, 1);
      }
    },
    removeByAccounts: (state, { payload: pkhs }) => fromPairs2(
      Object.entries(state).map(([networkName, batches]) => {
        const newBatches = (batches || []).filter(
          (batch) => !pkhs.includes(batch.sender.address.pkh)
        );
        return [networkName, newBatches];
      })
    )
  }
});
var batchesActions = batchesSlice.actions;

// src/slices/beacon.ts
import { createSlice as createSlice5 } from "@reduxjs/toolkit";
import { fromPairs as fromPairs3 } from "lodash";
var beaconInitialState = {};
var beaconSlice = createSlice5({
  name: "beacon",
  initialState: beaconInitialState,
  reducers: {
    reset: () => beaconInitialState,
    restore: (_, { payload }) => payload,
    addConnection: (state, { payload }) => {
      state[payload.dAppId] = { accountPkh: payload.accountPkh, networkType: payload.networkType };
    },
    removeConnection: (state, { payload: dAppId }) => {
      delete state[dAppId];
    },
    removeConnections: (state, { payload: pkhs }) => fromPairs3(
      Object.entries(state).filter(
        ([_, connectionInfo]) => !pkhs.includes(connectionInfo.accountPkh)
      )
    )
  }
});
var beaconActions = beaconSlice.actions;

// src/slices/contacts.ts
import { createSlice as createSlice6 } from "@reduxjs/toolkit";
var contactsInitialState = {};
var contactsSlice = createSlice6({
  name: "contacts",
  initialState: contactsInitialState,
  reducers: {
    reset: () => contactsInitialState,
    restore: (_, { payload }) => payload,
    // Make sure to check that
    // - pkh is not used in accounts and other contacts
    // - the name is unique across all accounts and contacts
    upsert: (state, { payload }) => {
      state[payload.pkh] = payload;
    },
    remove: (state, { payload }) => {
      delete state[payload];
    }
  }
});
var contactsActions = contactsSlice.actions;

// src/slices/errors.ts
import { createSlice as createSlice7 } from "@reduxjs/toolkit";
var MAX_ERRORS_LEN = 100;
var errorsInitialState = [];
var errorsSlice = createSlice7({
  name: "errors",
  initialState: errorsInitialState,
  reducers: {
    reset: () => errorsInitialState,
    restore: (_, { payload }) => payload,
    add(state, { payload }) {
      if (state.length === MAX_ERRORS_LEN) {
        state.shift();
      }
      state.push(payload);
    }
  }
});
var errorsActions = errorsSlice.actions;

// src/slices/multisigs.ts
import { createSlice as createSlice8 } from "@reduxjs/toolkit";
import { fromPairs as fromPairs4, groupBy as groupBy2, omit as omit2 } from "lodash";
var multisigsInitialState = {
  items: [],
  pendingOperations: {},
  labelsMap: {}
};
var multisigsSlice = createSlice8({
  name: "multisigs",
  initialState: multisigsInitialState,
  reducers: {
    reset: () => multisigsInitialState,
    restore: (_, { payload }) => payload,
    // to be used only for testing
    mockAddAccount: (state, { payload }) => {
      state.items.push({
        label: `Multisig Account ${state.items.length}`,
        type: "multisig",
        ...payload
      });
    },
    setMultisigs: (state, { payload }) => {
      const labelsByAddress = fromPairs4(
        state.items.map((multisig) => [multisig.address.pkh, multisig.label])
      );
      state.items = payload.map((multisig, i) => ({
        ...multisig,
        label: labelsByAddress[multisig.address.pkh] || state.labelsMap[multisig.address.pkh] || `Multisig Account ${i}`,
        type: "multisig"
      }));
    },
    addMultisigLabel: (state, { payload: { pkh, label } }) => {
      state.labelsMap[pkh] = label;
    },
    setPendingOperations: (state, { payload }) => {
      state.pendingOperations = groupBy2(payload, (operation) => operation.bigmapId);
    },
    // Do not call this directly, use the RenameAccount thunk
    setName: (state, { payload }) => {
      const {
        account: {
          address: { pkh }
        },
        newName
      } = payload;
      const account = state.items.find((multisig) => multisig.address.pkh === pkh);
      if (account) {
        account.label = newName;
      }
    },
    // Does not remove multisigs itself, they will be overwritten on the next data refetch.
    removeMultisigsData: (state, { payload: addresses }) => {
      const operationIdsToRemove = state.items.filter((multisig) => addresses.includes(multisig.address.pkh)).map((multisig) => String(multisig.pendingOperationsBigmapId));
      state.pendingOperations = omit2(state.pendingOperations, operationIdsToRemove);
      state.labelsMap = omit2(state.labelsMap, addresses);
    }
  }
});
var multisigsActions = multisigsSlice.actions;

// src/slices/networks.ts
import { createSlice as createSlice9 } from "@reduxjs/toolkit";
import {
  DefaultNetworks as DefaultNetworks2,
  MAINNET as MAINNET2,
  NETWORK_CONFIGS,
  isDefault,
  networks
} from "@umami/chains";
import { remove as remove2 } from "lodash";
var initialState = {
  configs: NETWORK_CONFIGS,
  showTestnets: false,
  // legacy
  available: DefaultNetworks2,
  current: MAINNET2,
  networks
};
var networksSlice = createSlice9({
  name: "networks",
  initialState,
  reducers: {
    reset: () => initialState,
    // Backups restore straight into the store without replaying migrations, so a backup
    // taken before a network was retired would bring its dead endpoints back. Strip them.
    restore: (_, { payload }) => withoutDeprecatedNetworks(payload),
    setCurrent: (state, { payload: network }) => {
      state.current = network;
    },
    upsertNetwork: (state, { payload: network }) => {
      if (isDefault(network)) {
        return;
      }
      const index = state.available.findIndex((n) => n.name === network.name);
      if (state.current.name === network.name) {
        state.current = network;
      }
      if (index !== -1) {
        state.available[index] = network;
        return;
      }
      state.available.push(network);
    },
    removeNetwork: (state, { payload: network }) => {
      if (isDefault(network)) {
        return;
      }
      remove2(state.available, (n) => n.name === network.name);
    }
  }
});
var networksActions = networksSlice.actions;

// src/slices/tokens.ts
import { createSlice as createSlice10 } from "@reduxjs/toolkit";
import { ChainFullIds, fromRawToken } from "@umami/chains";
import { DefaultNetworks as DefaultNetworks3 } from "@umami/tezos";
import { compact as compact2, fromPairs as fromPairs5, setWith } from "lodash";
var tokensInitialState = fromPairs5(
  DefaultNetworks3.map((network) => [network.name, {}])
);
var tokensSlice = createSlice10({
  name: "tokens",
  initialState: tokensInitialState,
  reducers: {
    reset: () => tokensInitialState,
    addTokens: (state, {
      payload: { network, tokens }
    }) => {
      compact2(tokens.map(fromRawToken)).forEach((token) => {
        setWith(
          state,
          [network.name, token.contract, token.tokenId],
          {
            ...token,
            chainId: ChainFullIds.TEZOS,
            id: `${token.contract}:${token.tokenId}`
          },
          Object
        );
      });
    }
  }
});
var tokensActions = tokensSlice.actions;

// src/slices/protocolSettings.ts
import { createSlice as createSlice11 } from "@reduxjs/toolkit";
import { DefaultNetworks as DefaultNetworks4 } from "@umami/tezos";
import { fromPairs as fromPairs6 } from "lodash";
var protocolSettingsInitialState = fromPairs6(
  DefaultNetworks4.map((network) => [network.name, { maxSlashingPeriod: 2, consensusRightsDelay: 2 }])
);
var protocolSettingsSlice = createSlice11({
  name: "protocolSettings",
  initialState: protocolSettingsInitialState,
  reducers: {
    update: (state, {
      payload: { network, settings }
    }) => {
      state[network.name] = settings;
    }
  }
});
var protocolSettingsActions = protocolSettingsSlice.actions;

// src/slices/WalletConnect.ts
import { createSlice as createSlice12 } from "@reduxjs/toolkit";
var wcInitialState = { peerListUpdatedToggle: false };
var wcSlice = createSlice12({
  name: "walletconnect",
  initialState: wcInitialState,
  reducers: {
    reset: () => wcInitialState,
    togglePeerListUpdated: (state) => {
      state.peerListUpdatedToggle = !state.peerListUpdatedToggle;
    }
  }
});
var wcActions = wcSlice.actions;

// src/slices/safeSendSecrets.ts
import { createSlice as createSlice13 } from "@reduxjs/toolkit";
var initialState2 = {
  secrets: {}
};
var safeSendSecretsSlice = createSlice13({
  name: "safeSendSecrets",
  initialState: initialState2,
  reducers: {
    addSafeSendSecret: (state, action) => {
      state.secrets[action.payload.hash] = action.payload.secret;
    },
    removeSafeSendSecret: (state, action) => {
      delete state.secrets[action.payload.hash];
    }
  }
});
var { addSafeSendSecret, removeSafeSendSecret } = safeSendSecretsSlice.actions;
var safeSendSecrets_default = safeSendSecretsSlice.reducer;

// src/slices/sapling.ts
import { createSlice as createSlice14 } from "@reduxjs/toolkit";
var saplingInitialState = {};
var saplingSlice = createSlice14({
  name: "sapling",
  initialState: saplingInitialState,
  reducers: {
    reset: () => saplingInitialState,
    restore: (_, { payload }) => payload,
    setAccount: (state, {
      payload: { id, data }
    }) => {
      state[id] = data;
    },
    updateAccount: (state, {
      payload: { id, data }
    }) => {
      if (state[id]) {
        state[id] = { ...state[id], ...data };
      }
    },
    removeAccount: (state, { payload: id }) => {
      delete state[id];
    },
    removeAccounts: (state, { payload: ids }) => {
      ids.forEach((id) => {
        delete state[id];
      });
    },
    clearBalances: (state) => {
      Object.keys(state).forEach((accountPkh) => {
        state[accountPkh].balance = "0";
      });
    }
  }
});
var saplingActions = saplingSlice.actions;

// src/store.ts
import { configureStore } from "@reduxjs/toolkit";
import { persistReducer, persistStore } from "redux-persist";

// src/reducer.ts
import { combineReducers } from "@reduxjs/toolkit";
import createWebStorage from "redux-persist/lib/storage/createWebStorage";
import { encryptTransform } from "redux-persist-transform-encrypt";

// src/createAsyncMigrate.ts
import { DEFAULT_VERSION } from "redux-persist";
function createAsyncMigrate(migrations, config) {
  const { debug } = config || {};
  return async (state, currentVersion) => {
    if (!state) {
      if (process.env.NODE_ENV !== "production" && debug) {
        console.log("redux-persist: no inbound state, skipping migration");
      }
      return void 0;
    }
    const inboundVersion = (
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      state._persist && state._persist.version !== void 0 ? state._persist.version : DEFAULT_VERSION
    );
    if (inboundVersion === currentVersion) {
      if (process.env.NODE_ENV !== "production" && debug) {
        console.log("redux-persist: versions match, noop migration");
      }
      return state;
    }
    if (inboundVersion > currentVersion) {
      if (process.env.NODE_ENV !== "production") {
        console.error("redux-persist: downgrading version is not supported");
      }
      return state;
    }
    const migrationKeys = Object.keys(migrations).map((ver) => parseInt(ver)).filter((key) => currentVersion >= key && key > inboundVersion).sort((a, b) => a - b);
    if (process.env.NODE_ENV !== "production" && debug) {
      console.log("redux-persist: migrationKeys", migrationKeys);
    }
    let migratedState = state;
    for (const versionKey of migrationKeys) {
      if (process.env.NODE_ENV !== "production" && debug) {
        console.log("redux-persist: running migration for versionKey", versionKey);
      }
      migratedState = await migrations[versionKey](migratedState);
    }
    return migratedState;
  };
}

// src/migrations.ts
import { nanoid } from "@reduxjs/toolkit";
import {
  ChainFullIds as ChainFullIds2,
  ChainNamespaces,
  DefaultNetworks as DefaultNetworks5,
  MAINNET as MAINNET3,
  NETWORK_CONFIGS as NETWORK_CONFIGS2,
  networks as currentNetworks,
  getChainFullIdFromNetwork as getChainFullIdFromNetwork2,
  parseChainFullId
} from "@umami/chains";
import { produce } from "immer";
import identity from "lodash/identity";
var VERSION = 17;
var mainStoreMigrations = {
  0: identity,
  1: (state) => produce(state, (draft) => {
    if (!draft.networks) {
      draft.networks = {};
    }
    draft.networks.configs = NETWORK_CONFIGS2;
  }),
  2: (state) => produce(state, (draft) => {
    if (draft.contacts) {
      Object.values(draft.contacts).forEach((contact) => {
        if (contact.chainId && !contact.chainNamespace) {
          try {
            const { namespace } = parseChainFullId(contact.chainId);
            contact.chainNamespace = namespace;
            delete contact.chainId;
          } catch {
            contact.chainNamespace = ChainNamespaces.TEZOS;
            delete contact.chainId;
          }
        }
      });
    }
  }),
  3: (state) => produce(state, (draft) => {
    if (draft.networks) {
      draft.networks.configs = NETWORK_CONFIGS2;
    }
  }),
  4: (state) => produce(state, (draft) => {
    if (draft.networks) {
      draft.networks.configs = NETWORK_CONFIGS2;
    }
  }),
  5: (state) => produce(state, (draft) => {
    if (draft.networks) {
      draft.networks.configs = NETWORK_CONFIGS2;
    }
  }),
  6: (state) => produce(state, (draft) => {
    if (draft.networks) {
      draft.networks.configs = NETWORK_CONFIGS2;
      draft.networks.networks = currentNetworks;
    }
  }),
  7: identity,
  8: identity,
  9: (state) => produce(state, (draft) => {
    if (!draft.networks) {
      return;
    }
    const mainnetSaplingAddress = NETWORK_CONFIGS2[ChainFullIds2.TEZOS].saplingContractAddress;
    const ghostnetSaplingAddress = "KT1ToBD7bovonshNrxs3i4KMFuZ8PE2LUmQf";
    const GHOSTNET_CHAIN_ID = "tezos:ghostnet";
    if (draft.networks.configs) {
      if (draft.networks.configs[ChainFullIds2.TEZOS] && !draft.networks.configs[ChainFullIds2.TEZOS].saplingContractAddress && mainnetSaplingAddress) {
        draft.networks.configs[ChainFullIds2.TEZOS].saplingContractAddress = mainnetSaplingAddress;
      }
      if (draft.networks.configs[GHOSTNET_CHAIN_ID] && !draft.networks.configs[GHOSTNET_CHAIN_ID].saplingContractAddress) {
        draft.networks.configs[GHOSTNET_CHAIN_ID].saplingContractAddress = ghostnetSaplingAddress;
      }
    }
    if (draft.networks.available && Array.isArray(draft.networks.available)) {
      draft.networks.available.forEach((network) => {
        if (network.name === "mainnet" && !network.saplingContractAddress && mainnetSaplingAddress) {
          network.saplingContractAddress = mainnetSaplingAddress;
        }
        if (network.name === "ghostnet" && !network.saplingContractAddress) {
          network.saplingContractAddress = ghostnetSaplingAddress;
        }
      });
    }
    if (draft.networks.current) {
      if (draft.networks.current.name === "mainnet" && !draft.networks.current.saplingContractAddress && mainnetSaplingAddress) {
        draft.networks.current.saplingContractAddress = mainnetSaplingAddress;
      }
      if (draft.networks.current.name === "ghostnet" && !draft.networks.current.saplingContractAddress) {
        draft.networks.current.saplingContractAddress = ghostnetSaplingAddress;
      }
    }
  }),
  10: (state) => produce(state, (draft) => {
    if (!draft.networks) {
      return;
    }
    if (draft.networks.configs) {
      Object.keys(NETWORK_CONFIGS2).forEach(
        (chainFullId) => {
          const latestConfig = NETWORK_CONFIGS2[chainFullId];
          const existingConfig = draft.networks.configs[chainFullId];
          if (existingConfig) {
            draft.networks.configs[chainFullId] = {
              ...existingConfig,
              ...latestConfig
            };
          } else {
            draft.networks.configs[chainFullId] = latestConfig;
          }
        }
      );
      Object.keys(draft.networks.configs).forEach((chainFullId) => {
        if (!(chainFullId in NETWORK_CONFIGS2)) {
          delete draft.networks.configs[chainFullId];
        }
      });
    } else {
      draft.networks.configs = NETWORK_CONFIGS2;
    }
    if (draft.networks.networks) {
      const updatedNetworks = { ...currentNetworks };
      Object.keys(draft.networks.networks).forEach((key) => {
        const currentNetwork = currentNetworks[key];
        if (currentNetwork) {
          updatedNetworks[key] = {
            ...draft.networks.networks[key],
            ...currentNetwork
          };
        }
      });
      draft.networks.networks = updatedNetworks;
    } else {
      draft.networks.networks = currentNetworks;
    }
  }),
  11: (state) => produce(state, (draft) => {
    if (!draft.networks) {
      return;
    }
    if (draft.networks.configs) {
      Object.keys(NETWORK_CONFIGS2).forEach(
        (chainFullId) => {
          const latestConfig = NETWORK_CONFIGS2[chainFullId];
          const existingConfig = draft.networks.configs[chainFullId];
          if (existingConfig) {
            draft.networks.configs[chainFullId] = {
              ...existingConfig,
              ...latestConfig
            };
          } else {
            draft.networks.configs[chainFullId] = latestConfig;
          }
        }
      );
      Object.keys(draft.networks.configs).forEach((chainFullId) => {
        if (!(chainFullId in NETWORK_CONFIGS2)) {
          delete draft.networks.configs[chainFullId];
        }
      });
    } else {
      draft.networks.configs = NETWORK_CONFIGS2;
    }
    if (draft.networks.networks) {
      const updatedNetworks = { ...currentNetworks };
      Object.keys(draft.networks.networks).forEach((key) => {
        const currentNetwork = currentNetworks[key];
        if (currentNetwork) {
          updatedNetworks[key] = {
            ...draft.networks.networks[key],
            ...currentNetwork
          };
        }
      });
      draft.networks.networks = updatedNetworks;
    } else {
      draft.networks.networks = currentNetworks;
    }
    if (draft.networks.available && Array.isArray(draft.networks.available)) {
      DefaultNetworks5.forEach((defaultNet) => {
        const index = draft.networks.available.findIndex((n) => n.name === defaultNet.name);
        if (index !== -1) {
          draft.networks.available[index] = {
            ...draft.networks.available[index],
            ...defaultNet
          };
        } else {
          draft.networks.available.push(defaultNet);
        }
      });
    } else {
      draft.networks.available = DefaultNetworks5;
    }
    if (draft.networks.current) {
      const matchingDefault = DefaultNetworks5.find((n) => n.name === draft.networks.current.name);
      if (matchingDefault) {
        draft.networks.current = {
          ...draft.networks.current,
          ...matchingDefault
        };
      }
    }
  }),
  12: (state) => produce(state, (draft) => {
    if (!draft.networks) {
      return;
    }
    const OLD_ID = "eip155:128123";
    const NEW_ID = ChainFullIds2.ETHERLINK_SHADOWNET;
    if (draft.networks.configs && draft.networks.configs[OLD_ID]) {
      draft.networks.configs[NEW_ID] = {
        ...draft.networks.configs[OLD_ID],
        ...NETWORK_CONFIGS2[NEW_ID]
      };
      delete draft.networks.configs[OLD_ID];
    }
    if (draft.networks.current && (draft.networks.current.chainId === OLD_ID || draft.networks.current.name === "Etherlink Testnet")) {
      draft.networks.current = {
        ...draft.networks.current,
        ...NETWORK_CONFIGS2[NEW_ID]
      };
    }
    if (draft.networks.available && Array.isArray(draft.networks.available)) {
      draft.networks.available.forEach((net, index) => {
        if (net.chainId === OLD_ID || net.name === "Etherlink Testnet") {
          draft.networks.available[index] = {
            ...net,
            ...NETWORK_CONFIGS2[NEW_ID]
          };
        }
      });
    }
  }),
  // Ghostnet has been discontinued. Remove it from persisted state and reset any user whose
  // selected network was ghostnet back to mainnet.
  13: (state) => produce(state, (draft) => {
    if (!draft.networks) {
      return;
    }
    const GHOSTNET_ID = "tezos:ghostnet";
    if (draft.networks.configs && draft.networks.configs[GHOSTNET_ID]) {
      delete draft.networks.configs[GHOSTNET_ID];
    }
    if (draft.networks.current && draft.networks.current.name === "ghostnet") {
      draft.networks.current = MAINNET3;
    }
    if (draft.networks.available && Array.isArray(draft.networks.available)) {
      draft.networks.available = draft.networks.available.filter(
        (net) => net.name !== "ghostnet"
      );
    }
  }),
  // Shadownet replaces the discontinued Ghostnet as the long-running Tezos testnet, and this
  // release also introduces the Michelson L2 (mainnet + shadownet variants) and Previewnet.
  // v13 already removed ghostnet (resetting current -> mainnet); this seeds every network the
  // app now exposes by default so upgrades from any earlier store version don't end up with a
  // half-populated `networks.configs` / `networks.available`.
  //
  // NOTE: as originally shipped (v0.2.0/v0.2.1) this migration seeded Previewnet with dead
  // endpoints (rpc.previewnet.teztnets.com never existed). NETWORK_CONFIGS now carries the
  // real ones, and v17 repairs stores that were already persisted with the dead ones.
  14: (state) => produce(state, (draft) => {
    if (!draft.networks) {
      return;
    }
    const newDefaultConfigs = [
      { id: ChainFullIds2.TEZOS_SHADOWNET, config: NETWORK_CONFIGS2[ChainFullIds2.TEZOS_SHADOWNET] },
      { id: ChainFullIds2.TEZOS_MICHELSON, config: NETWORK_CONFIGS2[ChainFullIds2.TEZOS_MICHELSON] },
      {
        id: ChainFullIds2.TEZOS_MICHELSON_SHADOWNET,
        config: NETWORK_CONFIGS2[ChainFullIds2.TEZOS_MICHELSON_SHADOWNET]
      },
      {
        id: ChainFullIds2.TEZOS_PREVIEWNET,
        config: NETWORK_CONFIGS2[ChainFullIds2.TEZOS_PREVIEWNET]
      }
    ];
    if (draft.networks.configs) {
      for (const { id, config } of newDefaultConfigs) {
        if (!draft.networks.configs[id]) {
          draft.networks.configs[id] = config;
        }
      }
    }
    if (draft.networks.available && Array.isArray(draft.networks.available)) {
      for (const { config } of newDefaultConfigs) {
        if (!draft.networks.available.some((net) => net.name === config.name)) {
          draft.networks.available.push(config);
        }
      }
    }
    const tezosLegacyNetworks = draft.networks.networks?.tezos;
    if (tezosLegacyNetworks) {
      if (Array.isArray(tezosLegacyNetworks.mainnet) && !tezosLegacyNetworks.mainnet.some(
        (net) => net.name === NETWORK_CONFIGS2[ChainFullIds2.TEZOS_MICHELSON].name
      )) {
        tezosLegacyNetworks.mainnet.push(NETWORK_CONFIGS2[ChainFullIds2.TEZOS_MICHELSON]);
      }
      if (Array.isArray(tezosLegacyNetworks.testnet)) {
        for (const { config } of newDefaultConfigs) {
          if (config.name !== NETWORK_CONFIGS2[ChainFullIds2.TEZOS_MICHELSON].name && !tezosLegacyNetworks.testnet.some((net) => net.name === config.name)) {
            tezosLegacyNetworks.testnet.push(config);
          }
        }
      }
    }
  }),
  // Ethereum Goerli was sunset by Ethereum in early 2025 and Blockscout dropped
  // its explorer, so proxy calls and direct RPC hit 404/CORS. Remove Goerli
  // from persisted state and redirect any user selecting it to Sepolia.
  15: (state) => produce(state, (draft) => {
    if (!draft.networks) {
      return;
    }
    const GOERLI_ID = "eip155:5";
    const SEPOLIA_ID = ChainFullIds2.ETHEREUM_SEPOLIA;
    const isGoerli = (net) => net?.chainId === GOERLI_ID || net?.identifier === "5" || net?.name === "Ethereum Goerli";
    if (draft.networks.configs && draft.networks.configs[GOERLI_ID]) {
      delete draft.networks.configs[GOERLI_ID];
    }
    if (draft.networks.current && isGoerli(draft.networks.current)) {
      draft.networks.current = (draft.networks.configs && draft.networks.configs[SEPOLIA_ID]) ?? NETWORK_CONFIGS2[SEPOLIA_ID] ?? MAINNET3;
    }
    if (draft.networks.available && Array.isArray(draft.networks.available)) {
      draft.networks.available = draft.networks.available.filter((net) => !isGoerli(net));
    }
    const ethLegacy = draft.networks.networks?.ethereum;
    if (ethLegacy && Array.isArray(ethLegacy.testnet)) {
      ethLegacy.testnet = ethLegacy.testnet.filter((net) => !isGoerli(net));
    }
  }),
  // Tezlink Shadownet was retired (#608 — the tezlink name is gone entirely, superseded by
  // Previewnet), which dropped it from the network configs without clearing persisted state.
  // Because the store keeps the whole network object (rpcUrl included), users who had it
  // selected keep polling the dead rpc.shadownet.tezlink.nomadic-labs.com /
  // api.shadownet.tezlink.tzkt.io hosts and see a stream of DNS and CORS failures — this is
  // what broke app.umamiwallet.com for returning users after the v0.2.x deploy. Remove it and
  // redirect anyone on it to Previewnet, its successor. (Note: NETWORK_CONFIGS previewnet now
  // carries the corrected endpoints, and v17 repairs any stale copy already in this store.)
  16: (state) => produce(state, (draft) => {
    if (!draft.networks) {
      return;
    }
    const TEZLINK_ID = "tezos:tezlink-shadownet";
    const PREVIEWNET_ID = ChainFullIds2.TEZOS_PREVIEWNET;
    const isTezlink = (net) => net?.chainId === TEZLINK_ID || net?.identifier === "tezlink-shadownet" || net?.name === "tezlink shadownet";
    if (draft.networks.configs && draft.networks.configs[TEZLINK_ID]) {
      delete draft.networks.configs[TEZLINK_ID];
    }
    if (draft.networks.current && isTezlink(draft.networks.current)) {
      draft.networks.current = (draft.networks.configs && draft.networks.configs[PREVIEWNET_ID]) ?? NETWORK_CONFIGS2[PREVIEWNET_ID] ?? MAINNET3;
    }
    if (draft.networks.available && Array.isArray(draft.networks.available)) {
      draft.networks.available = draft.networks.available.filter((net) => !isTezlink(net));
    }
    const tezosLegacy = draft.networks.networks?.tezos;
    if (tezosLegacy && Array.isArray(tezosLegacy.testnet)) {
      tezosLegacy.testnet = tezosLegacy.testnet.filter((net) => !isTezlink(net));
    }
  }),
  // Previewnet shipped in #608 (v0.2.0/v0.2.1) pointing at endpoints that never existed:
  // rpc.previewnet.teztnets.com and api.previewnet.tzkt.io have no DNS records. The network is
  // actually live under the tezosx domains (michelson.previewnet.tezosx.nomadic-labs.com /
  // api.previewnet.tezosx.tzkt.io). Stores persisted by v0.2.x carry the dead endpoints — any
  // user who selected previewnet got the same failure loop as Tezlink — so overwrite every
  // persisted copy of previewnet with the corrected config.
  17: (state) => produce(state, (draft) => {
    if (!draft.networks) {
      return;
    }
    const PREVIEWNET_ID = ChainFullIds2.TEZOS_PREVIEWNET;
    const previewnetConfig = NETWORK_CONFIGS2[PREVIEWNET_ID];
    const isPreviewnet = (net) => net?.chainId === PREVIEWNET_ID || net?.identifier === "previewnet" || net?.name === "previewnet";
    if (draft.networks.configs && draft.networks.configs[PREVIEWNET_ID]) {
      draft.networks.configs[PREVIEWNET_ID] = {
        ...draft.networks.configs[PREVIEWNET_ID],
        ...previewnetConfig
      };
    }
    if (draft.networks.current && isPreviewnet(draft.networks.current)) {
      draft.networks.current = { ...draft.networks.current, ...previewnetConfig };
    }
    if (draft.networks.available && Array.isArray(draft.networks.available)) {
      draft.networks.available.forEach((net, index) => {
        if (isPreviewnet(net)) {
          draft.networks.available[index] = { ...net, ...previewnetConfig };
        }
      });
    }
    const tezosLegacy = draft.networks.networks?.tezos;
    if (tezosLegacy && Array.isArray(tezosLegacy.testnet)) {
      tezosLegacy.testnet.forEach((net, index) => {
        if (isPreviewnet(net)) {
          tezosLegacy.testnet[index] = { ...net, ...previewnetConfig };
        }
      });
    }
  })
};
var accountsMigrations = {
  0: identity,
  1: (state) => produce(state, (draft) => {
    if (!draft.items) {
      return;
    }
    const networkMap = Object.fromEntries(
      Object.values(NETWORK_CONFIGS2).map((net) => [getChainFullIdFromNetwork2(net), net.chain])
    );
    draft.items.forEach((account) => {
      if (!account.id) {
        account.id = nanoid();
      }
      if (!account.chains) {
        return;
      }
      const newChains = {};
      for (const [fullId, chain] of Object.entries(networkMap)) {
        const chainData = account.chains[chain];
        if (chainData) {
          newChains[fullId] = chainData;
        }
      }
      account.chains = newChains;
    });
  }),
  2: identity,
  3: identity,
  4: identity,
  5: (state) => produce(state, (draft) => {
    if (!draft.items) {
      return;
    }
    draft.items.forEach((account) => {
      if (!account.chains) {
        return;
      }
      const knownConfigs = NETWORK_CONFIGS2;
      Object.keys(account.chains).forEach((chain) => {
        const config = knownConfigs[chain];
        if (!config) {
          return;
        }
        account.chains[chain].decimals = config.decimals;
      });
    });
  }),
  6: identity,
  7: (state) => produce(state, (draft) => {
    if (draft && typeof draft.passkeyRotationStatus === "undefined") {
      draft.passkeyRotationStatus = "Inactive";
    }
    if (draft && typeof draft.newEncryptedDek === "undefined") {
      draft.newEncryptedDek = void 0;
    }
  }),
  8: identity,
  9: identity,
  10: identity,
  11: (state) => produce(state, (draft) => {
    if (!draft.items) {
      return;
    }
    const shadownetFullId = ChainFullIds2.TEZOS_PREVIEWNET;
    const tezosMainnetFullId = ChainFullIds2.TEZOS;
    draft.items.forEach((account) => {
      if (!account.chains) {
        return;
      }
      if (account.chains[tezosMainnetFullId] && !account.chains[shadownetFullId]) {
        account.chains[shadownetFullId] = {
          ...account.chains[tezosMainnetFullId],
          decimals: NETWORK_CONFIGS2[shadownetFullId].decimals
        };
      }
    });
  }),
  12: (state) => produce(state, (draft) => {
    if (!draft.items) {
      return;
    }
    const OLD_ID = "eip155:128123";
    const NEW_ID = ChainFullIds2.ETHERLINK_SHADOWNET;
    draft.items.forEach((account) => {
      if (account.chains && account.chains[OLD_ID]) {
        account.chains[NEW_ID] = account.chains[OLD_ID];
        delete account.chains[OLD_ID];
      }
    });
  }),
  13: (state) => produce(state, (draft) => {
    if (!draft.items) {
      return;
    }
    const GHOSTNET_ID = "tezos:ghostnet";
    draft.items.forEach((account) => {
      if (account.chains && account.chains[GHOSTNET_ID]) {
        delete account.chains[GHOSTNET_ID];
      }
    });
  }),
  // Seed Shadownet account chain data (Shadownet replaced the discontinued Ghostnet).
  14: (state) => produce(state, (draft) => {
    if (!draft.items) {
      return;
    }
    const SHADOWNET_ID = ChainFullIds2.TEZOS_SHADOWNET;
    const tezosMainnetFullId = ChainFullIds2.TEZOS;
    draft.items.forEach((account) => {
      if (!account.chains) {
        return;
      }
      if (account.chains[tezosMainnetFullId] && !account.chains[SHADOWNET_ID]) {
        account.chains[SHADOWNET_ID] = {
          ...account.chains[tezosMainnetFullId],
          decimals: NETWORK_CONFIGS2[SHADOWNET_ID].decimals
        };
      }
    });
  }),
  // Drop persisted Ethereum Goerli chain data — network was sunset upstream.
  15: (state) => produce(state, (draft) => {
    if (!draft.items) {
      return;
    }
    const GOERLI_ID = "eip155:5";
    draft.items.forEach((account) => {
      if (account.chains && account.chains[GOERLI_ID]) {
        delete account.chains[GOERLI_ID];
      }
    });
  }),
  // Drop persisted Tezlink Shadownet chain data — network was retired upstream.
  16: (state) => produce(state, (draft) => {
    if (!draft.items) {
      return;
    }
    const TEZLINK_ID = "tezos:tezlink-shadownet";
    draft.items.forEach((account) => {
      if (account.chains && account.chains[TEZLINK_ID]) {
        delete account.chains[TEZLINK_ID];
      }
    });
  }),
  // Seed Previewnet account chain data from Tezos mainnet. Stores that upgraded from
  // version <= 10 got it from (the retargeted) migration v11, but stores at 11..16 ran the
  // original v11, which seeded the now-removed Tezlink instead — v16 deletes that, and this
  // fills the gap so every account exposes Previewnet.
  17: (state) => produce(state, (draft) => {
    if (!draft.items) {
      return;
    }
    const PREVIEWNET_ID = ChainFullIds2.TEZOS_PREVIEWNET;
    const tezosMainnetFullId = ChainFullIds2.TEZOS;
    draft.items.forEach((account) => {
      if (!account.chains) {
        return;
      }
      if (account.chains[tezosMainnetFullId] && !account.chains[PREVIEWNET_ID]) {
        account.chains[PREVIEWNET_ID] = {
          ...account.chains[tezosMainnetFullId],
          decimals: NETWORK_CONFIGS2[PREVIEWNET_ID].decimals
        };
      }
    });
  })
};

// src/slices/session.ts
import { createSlice as createSlice15 } from "@reduxjs/toolkit";
var sessionSlice = createSlice15({
  name: "session",
  initialState: {
    hasSession: false
  },
  reducers: {
    setHasSession: (state, action) => {
      state.hasSession = action.payload;
    }
  }
});
var { setHasSession } = sessionSlice.actions;

// src/reducer.ts
var TEST_STORAGE;
var getTestStorage = () => {
  if (!(process.env.NODE_ENV === "test" || process.env.CUCUMBER_WORKER_ID)) {
    return;
  }
  if (!TEST_STORAGE) {
    TEST_STORAGE = __require("redux-persist/lib/storage");
  }
  return TEST_STORAGE && "default" in TEST_STORAGE ? TEST_STORAGE.default : TEST_STORAGE;
};
var makeReducer = () => {
  const appReducer = combineReducers({
    accounts: accountsSlice.reducer,
    announcement: announcementSlice.reducer,
    assets: assetsSlice.reducer,
    batches: batchesSlice.reducer,
    beacon: beaconSlice.reducer,
    walletconnect: wcSlice.reducer,
    contacts: contactsSlice.reducer,
    errors: errorsSlice.reducer,
    multisigs: multisigsSlice.reducer,
    networks: networksSlice.reducer,
    protocolSettings: protocolSettingsSlice.reducer,
    tokens: tokensSlice.reducer,
    session: sessionSlice.reducer,
    safeSendSecrets: safeSendSecretsSlice.reducer,
    sapling: saplingSlice.reducer
  });
  return (state, action) => {
    if (action.type === "RESET_ALL") {
      state = void 0;
    }
    return appReducer(state, action);
  };
};
var makePersistConfigs = (storage_, password) => {
  const storage = storage_ || getTestStorage() || createWebStorage("local");
  if (!password) {
    return null;
  }
  const rootPersistConfig = {
    key: "root",
    version: VERSION,
    storage,
    blacklist: ["accounts", "assets", "announcement", "tokens", "protocolSettings", "session"],
    migrate: createAsyncMigrate(mainStoreMigrations, { debug: false }),
    transforms: [
      encryptTransform(
        {
          secretKey: password,
          onError: (error) => {
            console.error("Error encrypting root state:", error);
          }
        },
        {
          whitelist: ["contacts", "batches", "multisigs", "networks", "safeSendSecrets", "sapling"]
        }
      )
    ]
  };
  const accountsPersistConfig = {
    key: "accounts",
    version: VERSION,
    storage,
    migrate: createAsyncMigrate(accountsMigrations, { debug: false }),
    blacklist: ["password"],
    transforms: [
      encryptTransform(
        {
          secretKey: password,
          onError: (error) => {
            console.error("Error encrypting accounts state:", error);
          }
        },
        {
          blacklist: [
            "defaultAccount",
            "seedPhrases",
            "secretKeys",
            "passkeyRotationStatus",
            "newEncryptedDek"
          ]
        }
      )
    ]
  };
  return { rootPersistConfig, accountsPersistConfig };
};

// src/store.ts
var makeStore = () => {
  const rootReducer = makeReducer();
  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware({
      serializableCheck: {
        // Needed to remove warning
        // https://github.com/rt2zz/redux-persist/issues/988#issuecomment-552242978
        ignoredActions: [
          "persist/FLUSH",
          "persist/REHYDRATE",
          "persist/PAUSE",
          "persist/PERSIST",
          "persist/PURGE",
          "persist/REGISTER"
        ]
      }
    })
  });
};
var initializePersistence = (store, password, storage) => {
  const configs = makePersistConfigs(storage, password);
  if (!configs) {
    throw new Error("Failed to create persistence configuration");
  }
  const { rootPersistConfig, accountsPersistConfig } = configs;
  const rootReducer = makeReducer();
  const persistedRootReducer = persistReducer(rootPersistConfig, rootReducer);
  const persistedAccountsReducer = persistReducer(accountsPersistConfig, accountsSlice.reducer);
  const finalReducer = (state, action) => {
    const rootState = persistedRootReducer(state, action);
    if (state?.accounts) {
      const accountsState = persistedAccountsReducer(state.accounts, action);
      return { ...rootState, accounts: accountsState };
    }
    return rootState;
  };
  store.replaceReducer(finalReducer);
  const persistor = persistStore(store);
  return { persistor };
};

// src/testUtils.tsx
import { jsx } from "react/jsx-runtime";
var addTestAccount = (store, account) => {
  if (!("type" in account) || account.type === "multisig") {
    store.dispatch(multisigsActions.mockAddAccount(account));
    return;
  }
  store.dispatch(accountsActions.addAccount(account));
};
var addTestAccounts = (store, accounts) => {
  accounts.forEach((account) => addTestAccount(store, account));
};
var { waitFor } = testLib;
var mockToast = typeof jest !== "undefined" ? jest.fn() : function() {
};
mockToast.close = typeof jest !== "undefined" ? jest.fn() : function() {
};
mockToast.isActive = typeof jest !== "undefined" ? jest.fn() : function() {
};

// src/beacon/PeerInfo.ts
import { z } from "zod";
var parsePeerInfo = z.object({
  id: z.string(),
  type: z.string(),
  name: z.string(),
  publicKey: z.string(),
  version: z.string(),
  icon: z.string().optional(),
  relayServer: z.string().optional()
}).parse;

// src/beacon/WalletClient.ts
import { WalletClient as WalletClientClass } from "@tezos-x/octez.connect-wallet";
var WalletClient = typeof window === "undefined" || typeof window.localStorage === "undefined" ? {} : new WalletClientClass({
  // production
  name: "Umami",
  iconUrl: "",
  appUrl: "https://umamiwallet.com/"
});
var logout = async (store, persistor) => {
  try {
    await WalletClient.destroy();
  } catch (err) {
    console.error(err);
  }
  persistor.pause();
  store.dispatch({ type: "RESET_ALL" });
  await persistor.purge();
  localStorage.clear();
  sessionStorage.clear();
  window.location.replace("/");
};

// src/hooks/useAsyncActionHandler.ts
import { getErrorContext, useCustomToast } from "@umami/utils";
import { useCallback, useRef, useState } from "react";

// src/hooks/useAppDispatch.ts
import { useDispatch } from "react-redux";
var useAppDispatch = useDispatch;

// src/hooks/useAsyncActionHandler.ts
var useAsyncActionHandler = () => {
  const [isLoading, setIsLoading] = useState(false);
  const isLoadingRef = useRef(isLoading);
  const toast = useCustomToast();
  const dispatch = useAppDispatch();
  const handleAsyncActionUnsafe = useCallback(
    async (fn, toastOptions) => {
      if (isLoadingRef.current) {
        return;
      }
      isLoadingRef.current = true;
      setIsLoading(true);
      try {
        return await fn();
      } catch (error) {
        const errorContext = getErrorContext(error, true);
        if (!error.processed) {
          error.processed = true;
          toast({
            description: errorContext.description,
            status: "error",
            isClosable: true,
            ...typeof toastOptions === "function" ? toastOptions(error) : toastOptions
          });
          dispatch(errorsActions.add(errorContext));
        }
        throw error;
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    },
    [dispatch, toast]
  );
  const handleAsyncAction = useCallback(
    async (fn, toastOptions) => handleAsyncActionUnsafe(fn, toastOptions).catch(() => {
    }),
    [handleAsyncActionUnsafe]
  );
  return { isLoading, handleAsyncAction, handleAsyncActionUnsafe };
};

// src/hooks/useAppSelector.ts
import { useSelector } from "react-redux";
var useAppSelector = useSelector;

// src/hooks/useResetState.ts
var useResetState = () => {
  const dispatch = useAppDispatch();
  return () => dispatch({ type: "RESET_ALL" });
};

// src/hooks/assets.ts
import { mutezToTez } from "@umami/tezos";
import { BigNumber } from "bignumber.js";
import { compact as compact3, fromPairs as fromPairs7 } from "lodash";

// src/hooks/tokens.ts
import { get } from "lodash";

// src/hooks/network.ts
import { ChainIds } from "@umami/chains";
import { useDispatch as useDispatch2 } from "react-redux";
var useSelectedNetwork = () => useAppSelector((s) => s.networks.current);
var useBuyTezUrl = (pkh) => {
  const network = useSelectedNetwork();
  let buyTezUrl = network.buyTezUrl;
  if (buyTezUrl && network.name === "mainnet") {
    buyTezUrl += `/default/widget/?commodity=XTZ&address=${pkh}&network=tezos&commodity_id=xtz.simple.tezos`;
  }
  return buyTezUrl;
};
var useAvailableNetworks = () => useAppSelector((s) => s.networks.available);
var useAvailableMultichainNetworks = () => {
  const { configs } = useAppSelector((s) => s.networks);
  const mainnet = [];
  const testnet = [];
  for (const network of Object.values(configs)) {
    if (network.isTestnet) {
      testnet.push(network);
    } else {
      mainnet.push(network);
    }
  }
  return { mainnet, testnet, networks: Object.values(configs) };
};
var useFindNetwork = () => {
  const availableNetworks = useAvailableNetworks();
  return ({ type, name }) => availableNetworks.find(
    (network) => network.name.toLowerCase() === type.toLowerCase() || network.identifier.toLowerCase() === type.toLowerCase() || network.identifier.toLowerCase() === name?.toLowerCase() || network.name.toLowerCase() === name?.toLowerCase()
  );
};
var useSelectNetwork = () => {
  const availableNetworks = useAvailableNetworks();
  const dispatch = useDispatch2();
  return (name) => {
    const network = availableNetworks.find((network2) => network2.name === name);
    if (!network) {
      return;
    }
    dispatch(networksActions.setCurrent(network));
    dispatch(assetsActions.cleanAccountStates());
    dispatch(saplingActions.clearBalances());
  };
};
var useGetNetworkSymbol = () => {
  const { configs } = useAppSelector((s) => s.networks);
  return (chainFullId) => {
    const network = configs[chainFullId];
    return network.symbol;
  };
};
var useGetNetworkChain = () => {
  const { configs } = useAppSelector((s) => s.networks);
  return (chainFullId) => {
    const network = configs[chainFullId];
    return network.chain;
  };
};
var useGetNetworkByFullId = () => {
  const { configs } = useAppSelector((s) => s.networks);
  return (chainFullId) => configs[chainFullId];
};
var useGetNetworkName = () => {
  const { configs } = useAppSelector((s) => s.networks);
  return (chainFullId) => {
    const { name, chain } = configs[chainFullId];
    if (chain === ChainIds.TEZOS) {
      switch (name) {
        case "shadownet":
          return "Tezos Shadownet";
        case "michelson":
          return "Tezos Michelson";
        case "michelson shadownet":
          return "Tezos Michelson Shadownet";
        case "previewnet":
          return "Tezos Previewnet";
      }
      return "Tezos";
    }
    return name;
  };
};

// src/hooks/tokens.ts
var useGetToken = () => {
  const network = useSelectedNetwork();
  const tokens = useAppSelector((s) => s.tokens[network.name]);
  return (contract, tokenId) => get(tokens, [contract, tokenId]);
};
var useGetTokenType = (network) => {
  const tokens = useAppSelector((s) => s.tokens[network.name]);
  return (contract) => {
    if (!(tokens && contract in tokens)) {
      return void 0;
    }
    const contractTokens = tokens[contract];
    const anyTokenId = Object.keys(contractTokens)[0];
    if (!anyTokenId) {
      return void 0;
    }
    return contractTokens[anyTokenId].type;
  };
};

// src/hooks/assets.ts
var useGetAccountStates = () => useAppSelector((s) => s.assets.accountStates);
var useGetAccountState = () => {
  const accountStates = useGetAccountStates();
  return (pkh) => accountStates[pkh];
};
var useGetAccountDelegate = () => {
  const getAccountState = useGetAccountState();
  return (pkh) => getAccountState(pkh)?.delegate;
};
var useGetAccountBalance = () => {
  const getAccountState = useGetAccountState();
  return (pkh) => {
    const state = getAccountState(pkh);
    if (!state) {
      return void 0;
    }
    return state.balance;
  };
};
var useIsBlockFinalised = (level) => {
  const currentLevel = useAppSelector((s) => s.assets.block.level);
  return currentLevel ? currentLevel - level >= 2 : null;
};
var useAllNfts = () => {
  const getAccountNFTs = useGetAccountNFTs();
  const accountStates = useGetAccountStates();
  return fromPairs7(Object.keys(accountStates).map((address) => [address, getAccountNFTs(address)]));
};
var useGetAccountAssets = () => {
  const getToken = useGetToken();
  const getAccountState = useGetAccountState();
  return (pkh) => {
    const tokenBalances = getAccountState(pkh)?.tokens || [];
    return compact3(
      tokenBalances.map(({ contract, tokenId, ...rest }) => {
        const token = getToken(contract, tokenId);
        return token && { ...token, ...rest };
      })
    );
  };
};
var useGetAccountFA2Tokens = () => {
  const getAssets = useGetAccountAssets();
  return (pkh) => getAssets(pkh).filter((asset) => asset.type === "fa2");
};
var useGetAccountFA1Tokens = () => {
  const getAssets = useGetAccountAssets();
  return (pkh) => getAssets(pkh).filter((asset) => asset.type === "fa1.2");
};
var useGetAccountAllTokens = () => {
  const getFA1 = useGetAccountFA1Tokens();
  const getFA2 = useGetAccountFA2Tokens();
  return (pkh) => [...getFA1(pkh), ...getFA2(pkh)];
};
var useGetAccountNFTs = () => {
  const getAssets = useGetAccountAssets();
  return (pkh) => getAssets(pkh).filter((asset) => asset.type === "nft");
};
var useGetTokenTransfer = () => {
  const tokenTransfers = useAppSelector((s) => s.assets.transfers.tokens);
  return (transactionId) => {
    const transfer = tokenTransfers[transactionId];
    return transfer && { ...transfer, type: "token_transfer" };
  };
};
var useConversionRate = () => useAppSelector((s) => s.assets.conversionRate);
var useTezToDollar = () => {
  const rate = useConversionRate();
  if (!rate) {
    return () => void 0;
  }
  return (tezosBalanceTez) => BigNumber(tezosBalanceTez).multipliedBy(rate).decimalPlaces(2, BigNumber.ROUND_UP);
};
var useMutezToUsd = () => {
  const tezToDollar = useTezToDollar();
  return (tezosBalanceMutez) => tezToDollar(mutezToTez(tezosBalanceMutez).toFixed());
};
var useGetDollarBalance = () => {
  const tezToDollar = useTezToDollar();
  const getAccountBalance = useGetAccountBalance();
  return (pkh) => {
    const mutezBalance = getAccountBalance(pkh);
    return mutezBalance === void 0 ? void 0 : tezToDollar(mutezToTez(mutezBalance).toFixed());
  };
};
var useSpendableBalanceOfAllAccounts = () => {
  const accountStates = useGetAccountStates();
  const tezToDollar = useTezToDollar();
  const balances = Object.values(accountStates);
  if (balances.length === 0) {
    return null;
  }
  const totalBalance = balances.reduce((acc, curr) => acc.plus(curr?.balance || 0), BigNumber(0));
  const usdBalance = tezToDollar(mutezToTez(totalBalance).toFixed());
  return { mutez: totalBalance.toFixed(), usd: usdBalance };
};
var useBakerList = () => useAppSelector((state) => state.assets.bakers);
var useGetBaker = () => {
  const bakers = useBakerList();
  return (rawPkh) => bakers.find((baker) => baker.address === rawPkh);
};
var useIsLoading = () => useAppSelector((state) => state.assets.isLoading);
var useLastTimeUpdated = () => useAppSelector((state) => state.assets.lastTimeUpdated);

// src/hooks/backup.ts
import { encrypt as encrypt2 } from "@umami/crypto";
import { CustomError as CustomError4 } from "@umami/utils";
import Ajv from "ajv";
import { shallowEqual, useDispatch as useDispatch3, useSelector as useSelector2 } from "react-redux";
var ajv = new Ajv();
var backupSchema = {
  type: "object",
  properties: {
    accounts: {
      type: "object",
      properties: {
        defaultAccount: { type: "object" },
        items: { type: "array" },
        seedPhrases: { type: "object" },
        secretKeys: { type: "object" },
        current: { type: "string" },
        alerts: { type: "object" },
        password: { type: "string" }
      },
      required: [
        "defaultAccount",
        "items",
        "seedPhrases",
        "secretKeys",
        "current",
        "alerts",
        "password"
      ]
    },
    root: {
      type: "object",
      required: ["batches", "beacon", "contacts", "errors", "multisigs", "networks"]
    }
  },
  required: ["accounts", "root"]
};
var validateBackupSchema = ajv.compile(backupSchema);
var validateBackup = (backup) => {
  if (!validateBackupSchema(backup)) {
    console.error("Invalid backup file: " + ajv.errorsText(validateBackupSchema.errors));
    throw new CustomError4("Invalid backup file");
  }
};
var encryptSeedphrasesAndSecretkeys = async ({
  backup,
  password
}) => ({
  seedPhrases: await encryptKeyValuePairs({
    password,
    pairs: backup.accounts.seedPhrases
  }),
  secretKeys: await encryptKeyValuePairs({
    password,
    pairs: backup.accounts.secretKeys
  })
});
var decryptSeedphrasesAndSecretkeys = async ({
  password,
  accounts
}) => {
  const decryptedMnemonics = await decryptKeyValuePairs({
    password,
    pairs: accounts.seedPhrases
  });
  const decryptedSecretKeys = await decryptKeyValuePairs({
    password,
    pairs: accounts.secretKeys
  });
  return { decryptedMnemonics, decryptedSecretKeys };
};
var useRestoreBackup = () => {
  const dispatch = useDispatch3();
  return (backup) => {
    try {
      dispatch(accountsActions.restore(backup.accounts));
      const { batches, beacon, contacts, errors, multisigs, networks: networks2 } = backup.root;
      dispatch(batchesActions.restore(batches));
      dispatch(beaconActions.restore(beacon));
      dispatch(contactsActions.restore(contacts));
      dispatch(errorsActions.restore(errors));
      dispatch(multisigsActions.restore(multisigs));
      dispatch(networksActions.restore(networks2));
      return true;
    } catch (error) {
      console.error("Failed to restore backup:", error);
      throw new CustomError4("Failed to restore backup: " + error.message);
    }
  };
};
var useDownloadBackupFile = () => {
  const accounts = useSelector2((state) => state.accounts);
  const root = useSelector2(
    (state) => ({
      batches: state.batches,
      beacon: state.beacon,
      contacts: state.contacts,
      errors: state.errors,
      multisigs: state.multisigs,
      networks: state.networks
    }),
    shallowEqual
  );
  return async ({
    currentPassword,
    backupPassword
  }) => {
    if (currentPassword) {
      const { decryptedMnemonics, decryptedSecretKeys } = await decryptSeedphrasesAndSecretkeys({
        password: currentPassword,
        accounts
      });
      const backup = {
        accounts: {
          ...accounts,
          defaultAccount: {
            ...accounts.defaultAccount,
            // remove passkey-related attributes
            userId: void 0,
            dek: void 0
          },
          items: accounts.items.map((item) => ({
            ...item,
            // remove passkey-related attributes
            userId: void 0,
            dek: void 0
          })),
          seedPhrases: decryptedMnemonics,
          secretKeys: decryptedSecretKeys
        },
        root
      };
      const backupWithoutSocial = removeSocialAccounts(backup);
      const encryptedBackup = await encrypt2(JSON.stringify(backupWithoutSocial), backupPassword);
      const rawEncryptedBackup = JSON.stringify(encryptedBackup);
      const link = document.createElement("a");
      const currentDate = (/* @__PURE__ */ new Date()).toISOString().slice(0, 10);
      link.href = `data:text/json;charset=utf-8,${encodeURIComponent(rawEncryptedBackup)}`;
      link.download = `UmamiBackup_${currentDate}.json`;
      link.click();
    }
  };
};
var removeSocialAccounts = (backup) => {
  let defaultAccount = backup.accounts.defaultAccount;
  const shouldReplaceDefaultAccount = defaultAccount.type === "social";
  if (shouldReplaceDefaultAccount) {
    defaultAccount = backup.accounts.items.find((item) => item.type !== "social");
    defaultAccount = hideConfidentialData(defaultAccount);
  }
  return {
    ...backup,
    accounts: {
      ...backup.accounts,
      defaultAccount,
      items: backup.accounts.items.filter((item) => item.type !== "social")
    }
  };
};

// src/hooks/batches.ts
var useBatches = () => {
  const network = useSelectedNetwork();
  const batches = useAppSelector((s) => s.batches[network.name]);
  return batches || [];
};
var useClearBatch = () => {
  const dispatch = useAppDispatch();
  const network = useSelectedNetwork();
  return (account) => dispatch(batchesActions.clear({ pkh: account.address.pkh, network }));
};
var useRemoveBatchItem = () => {
  const dispatch = useAppDispatch();
  const network = useSelectedNetwork();
  return (account, index) => dispatch(batchesActions.removeItem({ pkh: account.address.pkh, index, network }));
};

// src/hooks/beacon.ts
import { useQuery } from "@tanstack/react-query";
import {
  Serializer
} from "@tezos-x/octez.connect-wallet";
import { useCustomToast as useCustomToast2 } from "@umami/utils";
import { uniq } from "lodash";
import { useDispatch as useDispatch4 } from "react-redux";
var useGetBeaconConnectionInfo = (dAppId) => {
  const beaconConnections = useAppSelector((s) => s.beacon);
  return beaconConnections[dAppId];
};
var useGetBeaconPeersForAccounts = () => {
  const beaconConnections = useAppSelector((s) => s.beacon);
  return (pkhs) => uniq(
    Object.entries(beaconConnections).filter(([_, connectionInfo]) => pkhs.includes(connectionInfo.accountPkh)).map(([dAppId, _]) => dAppId)
  );
};
var useResetBeaconConnections = () => {
  const dispatch = useDispatch4();
  return () => dispatch(beaconActions.reset());
};
var useAddBeaconConnection = () => {
  const dispatch = useDispatch4();
  return (dAppId, accountPkh, networkType) => dispatch(beaconActions.addConnection({ dAppId, accountPkh, networkType }));
};
var useRemoveBeaconConnection = () => {
  const dispatch = useDispatch4();
  return (dAppId) => dispatch(beaconActions.removeConnection(dAppId));
};
var useBeaconPeers = () => {
  const query = useQuery({
    queryKey: ["beaconPeers"],
    queryFn: async () => {
      const beaconPeers = await WalletClient.getPeers();
      return beaconPeers;
    },
    initialData: []
  });
  return { peers: query.data, refresh: query.refetch };
};
var useRemoveBeaconPeer = () => {
  const { refresh } = useBeaconPeers();
  const removeConnectionFromBeaconSlice = useRemoveBeaconConnection();
  return (peerInfo) => WalletClient.removePeer(peerInfo, true).then(() => removeConnectionFromBeaconSlice(peerInfo.senderId)).finally(() => void refresh());
};
var useRemoveBeaconPeerBySenderId = () => {
  const { peers } = useBeaconPeers();
  const removePeer = useRemoveBeaconPeer();
  return (senderId) => Promise.all(peers.filter((peerInfo) => senderId === peerInfo.senderId).map(removePeer));
};
var useRemoveBeaconPeersByAccounts = () => {
  const getPeersForAccounts = useGetBeaconPeersForAccounts();
  const removePeerBySenderId = useRemoveBeaconPeerBySenderId();
  return (pkhs) => Promise.all(getPeersForAccounts(pkhs).map(removePeerBySenderId));
};
var useAddPeer = () => {
  const { refresh } = useBeaconPeers();
  const toast = useCustomToast2();
  return (payload) => new Serializer().deserialize(payload).then(parsePeerInfo).then((peer) => WalletClient.addPeer(peer)).then(() => refresh()).catch(() => {
    const description = "To connect, open the dApp\u2019s connect menu and copy the QR code. Look for a Beacon or WalletConnect option.";
    const type = "info";
    toast.show ? toast.show({
      type,
      text1: description
    }) : toast({
      description,
      status: type
    });
  });
};

// src/hooks/contacts.ts
import {
  ChainFullIds as ChainFullIds4,
  ChainNamespaces as ChainNamespaces2,
  isBitcoinAddressValid,
  isEvmAddressValid,
  isSaplingAddress,
  parseChainFullId as parseChainFullId2
} from "@umami/chains";
import { isAddressValid, isValidImplicitPkh } from "@umami/tezos";

// src/hooks/getAccountData.ts
import {
  ChainFullIds as ChainFullIds3,
  getTokenSymbol
} from "@umami/chains";
import { decrypt as decrypt2 } from "@umami/crypto";
import { forIDP } from "@umami/social-auth";
import { deriveSecretKey } from "@umami/tezos";
import { CustomError as CustomError5 } from "@umami/utils";
import { maxBy } from "lodash";
import { useEffect } from "react";
import { useDispatch as useDispatch5 } from "react-redux";

// src/hooks/multisig.ts
var useMultisigAccounts = () => useAppSelector((s) => s.multisigs.items);
var useGetPendingMultisigOperations = () => {
  const pendingOperations = useAppSelector((s) => s.multisigs.pendingOperations);
  return (account) => {
    const pendings = pendingOperations[account.pendingOperationsBigmapId] ?? [];
    return [...pendings].sort((a, b) => Number(b.id) - Number(a.id));
  };
};

// src/hooks/getAccountData.ts
var useSeedPhrases = () => useAppSelector((s) => s.accounts.seedPhrases);
var useSecretKeys = () => useAppSelector((s) => s.accounts.secretKeys);
var useImplicitAccounts = () => useAppSelector((s) => s.accounts.items);
var useGetImplicitAccountSafe = () => {
  const accounts = useImplicitAccounts();
  return (pkh) => accounts.find(
    (account) => account.address.pkh.toLowerCase() === pkh.toLowerCase() || account.chains && Object.values(account.chains).some(
      (chain) => chain?.address.pkh.toLowerCase() === pkh.toLowerCase()
    )
  );
};
var useGetImplicitAccount = () => {
  const getAccount = useGetImplicitAccountSafe();
  return (pkh) => {
    const account = getAccount(pkh);
    if (!account) {
      throw new CustomError5(`Unknown account: ${pkh}`);
    }
    return account;
  };
};
var useGetAccountsByType = () => {
  const accounts = useImplicitAccounts();
  return (type) => accounts.filter((account) => account.type === type);
};
var useGetAccountsByFingerPrint = () => {
  const getAccountsByType = useGetAccountsByType();
  return (fingerPrint) => getAccountsByType("mnemonic").filter(
    (account) => account.seedFingerPrint === fingerPrint
  );
};
var useAllAccounts = () => {
  const implicit = useImplicitAccounts();
  const multisig = useMultisigAccounts();
  return [...implicit, ...multisig];
};
var useGetOwnedAccountSafe = () => {
  const accounts = useAllAccounts();
  return (pkh, chainId = ChainFullIds3.TEZOS) => accounts.find(
    (account) => account.address.pkh === pkh || account.chains?.[chainId]?.address.pkh === pkh
  );
};
var useGetOwnedAccount = () => {
  const getOwnedAccount = useGetOwnedAccountSafe();
  return (pkh, chainId = ChainFullIds3.TEZOS) => {
    const account = getOwnedAccount(pkh, chainId);
    if (!account) {
      throw new CustomError5(`Unknown account: ${pkh}`);
    }
    return account;
  };
};
var useValidateMasterPassword = () => {
  const seedPhrases = useSeedPhrases();
  const secretKeys = useSecretKeys();
  const encrypted = Object.values({ ...secretKeys, ...seedPhrases })[0];
  if (!encrypted) {
    return null;
  }
  return async (password) => {
    await decrypt2(encrypted, password);
    return true;
  };
};
var useIsPasswordSet = () => !!useValidateMasterPassword();
var useIsOwnedAddress = () => {
  const ownedAccounts = useAllAccounts();
  const addressesSet = new Set(ownedAccounts.map((acc) => acc.address.pkh));
  return (address) => !!address && addressesSet.has(address);
};
var useGetMultisigSigners = () => {
  const implicitAccounts = useImplicitAccounts();
  return (multisigAccount) => {
    const signers = implicitAccounts.filter(
      (implicitAccount) => multisigAccount.signers.some((signer) => signer.pkh === implicitAccount.address.pkh)
    );
    if (signers.length === 0) {
      console.warn(
        "Wallet doesn't own any signers for multisig contract " + multisigAccount.address.pkh
      );
    }
    return signers;
  };
};
var useGetOwnedSignersForAccount = () => {
  const getMultisigSigners = useGetMultisigSigners();
  return (account) => {
    switch (account.type) {
      case "ledger":
      case "mnemonic":
      case "social":
      case "secret_key":
        return [account];
      case "multisig":
        return getMultisigSigners(account);
    }
  };
};
var useGetBestSignerForAccount = () => {
  const getSigners = useGetOwnedSignersForAccount();
  const getMostFundedImplicitAccount = useGetMostFundedImplicitAccount();
  return (account) => getMostFundedImplicitAccount(getSigners(account));
};
var useGetMostFundedImplicitAccount = () => {
  const getAccountBalance = useGetAccountBalance();
  return (accounts) => maxBy(accounts, (signer) => Number(getAccountBalance(signer.address.pkh) || "0"));
};
var useGetSecretKey = () => {
  const encryptedSecretKeys = useSecretKeys();
  const getDecryptedMnemonic = useGetDecryptedMnemonic();
  return async (account, password) => {
    if (account.type === "secret_key") {
      const encryptedSecretKey = encryptedSecretKeys[account.address.pkh];
      if (!encryptedSecretKey) {
        throw new CustomError5(`Missing secret key for account ${account.address.pkh}`);
      }
      return {
        secretKey: await decrypt2(encryptedSecretKey, password)
      };
    }
    const mnemonic = await getDecryptedMnemonic(account, password);
    return {
      secretKey: await deriveSecretKey(mnemonic, account.derivationPath, account.curve),
      mnemonic,
      derivationPath: account.derivationPath
    };
  };
};
var useGetMultichainSecretKey = () => {
  const encryptedSecretKeys = useSecretKeys();
  const getDecryptedMnemonic = useGetDecryptedMnemonic();
  return async (account, password) => {
    if (account.type === "social") {
      const { secretKey, id } = await forIDP(account.idp).getCredentials();
      if (id !== account.label) {
        throw new CustomError5(
          `Wrong account selected: ${id}, expecting account with email ${account.label}`
        );
      }
      return {
        secretKey
      };
    }
    if (account.type === "secret_key") {
      const encryptedSecretKey = encryptedSecretKeys[account.address.pkh];
      if (!encryptedSecretKey) {
        throw new CustomError5(`Missing secret key for account ${account.address.pkh}`);
      }
      return {
        secretKey: await decrypt2(encryptedSecretKey, password)
      };
    }
    return {
      mnemonic: await getDecryptedMnemonic(account, password)
    };
  };
};
var useCurrentAccount = () => {
  const currentAccountAddress = useAppSelector((s) => s.accounts.current);
  const dispatch = useDispatch5();
  const accounts = useImplicitAccounts();
  const currentAccount = accounts.find((account) => account.address.pkh === currentAccountAddress);
  useEffect(() => {
    if (!currentAccount) {
      dispatch(accountsActions.setCurrent(accounts[0]?.address.pkh));
    }
  }, [currentAccount, currentAccountAddress, dispatch, accounts]);
  return currentAccount;
};
var useDefaultAccount = () => {
  const defaultAccount = useAppSelector((s) => s.accounts.defaultAccount);
  if (!defaultAccount) {
    throw new CustomError5("Default account not found. This should never happen.");
  }
  return defaultAccount;
};
var useGetDecryptedMnemonic = () => {
  const seedPhrases = useSeedPhrases();
  return async (account, password) => {
    const encryptedMnemonic = seedPhrases[account.seedFingerPrint];
    if (!encryptedMnemonic) {
      throw new CustomError5(`Missing seedphrase for account ${account.address.pkh}`);
    }
    return decrypt2(encryptedMnemonic, password);
  };
};
var useGetUserAlerts = () => {
  const alerts = useAppSelector((s) => s.accounts.alerts);
  return (key) => alerts[key];
};
var useGetDefaultAccount = () => useAppSelector((s) => s.accounts.defaultAccount);
var useGetAssetPrice = () => {
  const prices = useAppSelector((s) => s.accounts.prices);
  return (asset = "") => {
    const symbol = typeof asset === "string" ? asset : getTokenSymbol(asset);
    if (!symbol) {
      return 0;
    }
    return prices[symbol] ?? 0;
  };
};

// src/hooks/contacts.ts
var useAllContacts = () => useAppSelector((s) => s.contacts);
var useContactsForSelectedNetwork = () => {
  const network = useSelectedNetwork();
  const allContacts = useAllContacts();
  return Object.values(allContacts).filter((contact) => {
    const isSelectedNetwork = contact.network === network.name;
    const { chainNamespace: namespace } = contact;
    let isValidAddress = false;
    switch (namespace) {
      // TODO: Refactor this in the future to use the ChainRegistryClass
      case ChainNamespaces2.TEZOS:
        isValidAddress = isValidImplicitPkh(contact.pkh) || isSaplingAddress(contact.pkh);
        break;
      case ChainNamespaces2.EVM:
        isValidAddress = isEvmAddressValid(contact.pkh);
        break;
      case ChainNamespaces2.BITCOIN:
        isValidAddress = isBitcoinAddressValid(contact.pkh);
        break;
    }
    return isValidAddress || isSelectedNetwork;
  }).map((contact) => contact);
};
var useSortedContacts = () => {
  const contactsForSelectedNetwork = useContactsForSelectedNetwork();
  return contactsForSelectedNetwork.sort((a, b) => a.name.localeCompare(b.name)).map((contact) => contact);
};
var useAddressExistsInContacts = () => {
  const contacts = useAllContacts();
  return (pkh) => pkh in contacts;
};
var useGetContactName = () => {
  const contacts = useAllContacts();
  return (pkh) => pkh in contacts ? contacts[pkh].name : void 0;
};
var useValidateNewContactPkh = (chainId = ChainFullIds4.TEZOS) => {
  const addressExistsInContacts = useAddressExistsInContacts();
  const getNetworkByFullId = useGetNetworkByFullId();
  const getAccount = useGetOwnedAccountSafe();
  const { namespace } = parseChainFullId2(chainId);
  return (pkh) => {
    switch (namespace) {
      // TODO: Refactor this in the future to use the ChainRegistryClass (https://linear.app/tezos/issue/UMA-1708)
      case ChainNamespaces2.TEZOS:
        if (!isAddressValid(pkh) && !isSaplingAddress(pkh)) {
          return "Invalid tezos address";
        }
        break;
      case ChainNamespaces2.EVM:
        if (!isEvmAddressValid(pkh)) {
          return `Invalid ${getNetworkByFullId(chainId).chain} address`;
        }
        break;
      case ChainNamespaces2.BITCOIN:
        if (!isBitcoinAddressValid(pkh)) {
          return `Invalid ${getNetworkByFullId(chainId).chain} address`;
        }
        break;
    }
    if (getAccount(pkh)) {
      return "Address is already used in accounts";
    }
    return !addressExistsInContacts(pkh) || "Address is already registered";
  };
};
var useValidateNewContactPkhByNamespace = (namespace = ChainNamespaces2.TEZOS) => {
  const addressExistsInContacts = useAddressExistsInContacts();
  const getAccount = useGetOwnedAccountSafe();
  return (pkh) => {
    const invalidAddressError = "Invalid address";
    switch (namespace) {
      // TODO: Refactor this in the future to use the ChainRegistryClass (https://linear.app/tezos/issue/UMA-1708)
      case ChainNamespaces2.TEZOS:
        if (!isAddressValid(pkh) && !isSaplingAddress(pkh)) {
          return invalidAddressError;
        }
        break;
      case ChainNamespaces2.EVM:
        if (!isEvmAddressValid(pkh)) {
          return invalidAddressError;
        }
        break;
      case ChainNamespaces2.BITCOIN:
        if (!isBitcoinAddressValid(pkh)) {
          return invalidAddressError;
        }
        break;
    }
    if (getAccount(pkh)) {
      return "Address is already used in accounts";
    }
    return !addressExistsInContacts(pkh) || "Address is already registered";
  };
};

// src/hooks/labels.ts
var PROHIBITED_CHARACTERS = "/\\\"'<>:{}$#|`	\r\n".split("");
var useValidateName = (oldName) => {
  const isUniqueLabel = useIsUniqueLabel();
  return (name) => {
    const trimmedName = name.trim();
    if (trimmedName.length > 256) {
      return "Name should not exceed 256 characters";
    }
    if (PROHIBITED_CHARACTERS.some((char) => trimmedName.includes(char))) {
      return "Name contains special character(s)";
    }
    if (trimmedName.length === 0) {
      return "Name should not be empty";
    }
    if (oldName !== void 0 && trimmedName === oldName) {
      return "Name was not changed";
    }
    if (!isUniqueLabel(trimmedName)) {
      return "Name must be unique across all accounts and contacts";
    }
    return true;
  };
};
var useGetNextAvailableAccountLabels = () => {
  const isUniqueLabel = useIsUniqueLabel();
  return (labelPrefix, count = 1) => {
    const labels = [];
    for (let index = 1; labels.length < count; index++) {
      const nextLabel = index === 1 ? labelPrefix : `${labelPrefix} ${index}`;
      if (isUniqueLabel(nextLabel)) {
        labels.push(nextLabel);
      }
    }
    return labels;
  };
};
var useIsUniqueLabel = () => {
  const accountLabels = useAllAccounts().map((account) => account.label);
  const contactNames = Object.values(useAllContacts()).map((contact) => contact.name);
  return (label) => ![...accountLabels, ...contactNames].includes(label);
};

// src/hooks/mnemonic.ts
import { nanoid as nanoid2 } from "@reduxjs/toolkit";
import {
  derivePublicKeyPair,
  generateHash,
  isAccountRevealed,
  makeDerivationPath
} from "@umami/chains";
import { generateMnemonic } from "bip39";
var generate24WordMnemonic = () => generateMnemonic(256);
var restoreRevealedPublicKeyPairs = async ({
  mnemonic,
  derivationPathTemplate,
  derivationPath,
  curve,
  network
}) => {
  const result = [];
  let accountIndex = 0;
  let pubKeyPair = await derivePublicKeyPair(
    mnemonic,
    makeDerivationPath(derivationPathTemplate, accountIndex, derivationPath),
    curve
  );
  do {
    result.push(pubKeyPair);
    accountIndex += 1;
    pubKeyPair = await derivePublicKeyPair(
      mnemonic,
      makeDerivationPath(derivationPathTemplate, accountIndex, derivationPath),
      curve
    );
  } while (await isAccountRevealed(pubKeyPair.pkh, network));
  return result;
};
var useRestoreRevealedMnemonicAccounts = () => {
  const getNextAvailableAccountLabels = useGetNextAvailableAccountLabels();
  return async ({
    mnemonic,
    network,
    derivationPathTemplate,
    derivationPath,
    label,
    curve,
    isVerified = true,
    encrypted_dek,
    userId
  }) => {
    const pubKeyPairs = await restoreRevealedPublicKeyPairs({
      mnemonic,
      derivationPathTemplate,
      derivationPath,
      curve,
      network
    });
    const seedFingerPrint = generateHash();
    const accountLabels = getNextAvailableAccountLabels(label, pubKeyPairs.length);
    return pubKeyPairs.map(({ pk, pkh }, accountIndex) => ({
      id: nanoid2(),
      type: "mnemonic",
      seedFingerPrint,
      label: accountLabels[accountIndex],
      isVerified,
      address: { type: "implicit", pkh },
      derivationPath: makeDerivationPath(derivationPathTemplate, accountIndex, derivationPath),
      derivationPathTemplate,
      curve,
      pk,
      encrypted_dek,
      userId
    }));
  };
};

// src/hooks/operations.ts
var useGetOperationDestination = (sender, receiver) => {
  const isOwned = useIsOwnedAddress();
  const isSenderOwned = isOwned(sender);
  const isReceiverOwned = isOwned(receiver);
  if (!isSenderOwned && !isReceiverOwned) {
    return "unrelated";
  }
  if (isSenderOwned) {
    return "outgoing";
  }
  return "incoming";
};

// src/hooks/protocolSettings.ts
var useGetProtocolSettings = () => {
  const network = useSelectedNetwork();
  return useAppSelector((state) => state.protocolSettings[network.name]);
};

// src/hooks/removeAccountDependencies.ts
var useRemoveDependenciesAndMultisigs = () => {
  const dispatch = useAppDispatch();
  const getMultisigsToRemove = useGetMultisigsToRemove();
  const removeAccountsDependencies = useRemoveAccountsDependencies();
  return (accountsToRemove) => {
    const multisigsToRemove = getMultisigsToRemove(accountsToRemove);
    removeAccountsDependencies([...accountsToRemove, ...multisigsToRemove]);
    dispatch(multisigsActions.removeMultisigsData(multisigsToRemove.map((m) => m.address.pkh)));
  };
};
var useRemoveAccountsDependencies = () => {
  const dispatch = useAppDispatch();
  const removePeersByAccounts = useRemoveBeaconPeersByAccounts();
  const currentAccount = useCurrentAccount();
  const implicitAccounts = useImplicitAccounts();
  return (accounts) => {
    const pkhs = accounts.map((account) => account.address.pkh);
    dispatch(batchesActions.removeByAccounts(pkhs));
    void removePeersByAccounts(pkhs);
    dispatch(beaconActions.removeConnections(pkhs));
    dispatch(assetsActions.removeAccountsData(pkhs));
    dispatch(saplingActions.removeAccounts(pkhs));
    if (!accounts.find((acc) => acc.address.pkh === currentAccount?.address.pkh)) {
      return;
    }
    const remainingAccount = implicitAccounts.find((account) => !pkhs.includes(account.address.pkh));
    dispatch(accountsActions.setCurrent(remainingAccount?.address.pkh));
  };
};
var useGetMultisigsToRemove = () => {
  const implicit = useImplicitAccounts();
  const multisig = useMultisigAccounts();
  return (accountsToRemove) => {
    const deletedPkhs = accountsToRemove.map((account) => account.address.pkh);
    const remainingPkhs = implicit.map((account) => account.address.pkh).filter((address) => !deletedPkhs.includes(address));
    return multisig.filter(
      (multisig2) => !multisig2.signers.some((signer) => remainingPkhs.includes(signer.pkh))
    );
  };
};

// src/hooks/setAccountData.ts
import { nanoid as nanoid4 } from "@reduxjs/toolkit";
import {
  ChainFullIds as ChainFullIds5,
  derivePublicKeyPair as derivePublicKeyPair2,
  getDerivationPathNextIndex,
  makeDerivationPath as makeDerivationPath2
} from "@umami/chains";
import { decrypt as decrypt3, encrypt as encrypt4 } from "@umami/crypto";
import { CustomError as CustomError7 } from "@umami/utils";
import { useDispatch as useDispatch6 } from "react-redux";

// src/thunks/estimateAndUpdateBatch.ts
import { estimate } from "@umami/chains";
var estimateAndUpdateBatch = (operations, network) => async (dispatch) => {
  await estimate(operations, network);
  dispatch(batchesActions.add({ operations, network }));
};

// src/thunks/renameAccount.ts
var renameAccount = (account, newName) => (dispatch, getState) => {
  const { accounts, multisigs, contacts } = getState();
  const isMultisig = account.type === "multisig";
  const accountNames = accounts.items.map((account2) => account2.label);
  const multisigNames = multisigs.items.map((multisig) => multisig.label);
  const contactNames = Object.values(contacts).map((contact) => contact.name);
  if ([accountNames, multisigNames, contactNames].flat().includes(newName)) {
    return;
  }
  if (isMultisig) {
    dispatch(multisigsActions.setName({ newName, account }));
  } else {
    dispatch(accountsActions.renameAccount({ newName, account }));
  }
};

// src/thunks/secretKeyAccount.ts
import { nanoid as nanoid3 } from "@reduxjs/toolkit";
import { PrefixV2 } from "@taquito/utils";
import { encrypt as encrypt3 } from "@umami/crypto";
import { getPublicKeyPairFromSk, parseImplicitPkh } from "@umami/tezos";
import { CustomError as CustomError6 } from "@umami/utils";
var getCurve = (secretKey) => {
  if (secretKey.startsWith(PrefixV2.Ed25519EncryptedSeed) || secretKey.startsWith(PrefixV2.Ed25519Seed)) {
    return "ed25519";
  }
  if (secretKey.startsWith(PrefixV2.Secp256k1EncryptedSecretKey) || secretKey.startsWith(PrefixV2.Secp256k1SecretKey)) {
    return "secp256k1";
  }
  if (secretKey.startsWith(PrefixV2.P256EncryptedSecretKey) || secretKey.startsWith(PrefixV2.P256SecretKey)) {
    return "p256";
  }
  throw new CustomError6("Invalid secret key");
};
var isEncryptedSecretKeyPrefix = (secretKeyPrefix) => secretKeyPrefix.substring(2, 3) === "e";
var makeSecretKeyAccount = async ({
  secretKey,
  label,
  password,
  encrypted_dek,
  userId
}) => {
  const { pk, pkh } = await getPublicKeyPairFromSk(secretKey);
  const encryptedSecretKey = await encrypt3(secretKey, password);
  const account = {
    type: "secret_key",
    pk,
    label,
    curve: getCurve(secretKey),
    address: parseImplicitPkh(pkh),
    encrypted_dek,
    userId,
    id: nanoid3()
  };
  return {
    account,
    encryptedSecretKey
  };
};
var restoreFromSecretKey = ({
  secretKey,
  label,
  password,
  encrypted_dek,
  userId
}) => async (dispatch) => {
  const { account, encryptedSecretKey } = await makeSecretKeyAccount({
    secretKey,
    label,
    password,
    encrypted_dek,
    userId
  });
  dispatch(accountsActions.addAccount(account));
  dispatch(accountsActions.setDefaultAccount());
  dispatch(accountsActions.addSecretKey({ pkh: account.address.pkh, encryptedSecretKey }));
  return account;
};

// src/hooks/setAccountData.ts
var { removeMnemonicAndAccounts, removeNonMnemonicAccounts, addAccount } = accountsActions;
var useRestoreFromMnemonic = () => {
  const network = useSelectedNetwork();
  const restoreRevealedMnemonicAccounts = useRestoreRevealedMnemonicAccounts();
  return async ({
    mnemonic,
    password,
    derivationPathTemplate,
    derivationPath,
    label,
    curve,
    isVerified = true,
    encrypted_dek,
    userId
  }) => {
    const accounts = await restoreRevealedMnemonicAccounts({
      mnemonic,
      network,
      derivationPathTemplate,
      derivationPath,
      label,
      curve,
      isVerified,
      encrypted_dek,
      userId
    });
    const encryptedMnemonic = await encrypt4(mnemonic, password);
    return { accounts, encryptedMnemonic };
  };
};
var useDeriveMnemonicAccount = () => {
  const encryptedMnemonics = useSeedPhrases();
  const implicitAccounts = useImplicitAccounts();
  const getNextAvailableAccountLabels = useGetNextAvailableAccountLabels();
  const dispatch = useDispatch6();
  return async ({
    fingerPrint,
    password,
    label,
    derivationPath,
    curve
  }) => {
    const encryptedSeedphrase = encryptedMnemonics[fingerPrint];
    if (!encryptedSeedphrase) {
      throw new CustomError7(`No seedphrase found with fingerprint: ${fingerPrint}`);
    }
    const seedphrase = await decrypt3(encryptedSeedphrase, password);
    const existingGroupAccounts = implicitAccounts.filter(
      (acc) => acc.type === "mnemonic" && acc.seedFingerPrint === fingerPrint
    );
    const { derivationPathTemplate, curve: existingCurve } = existingGroupAccounts[0];
    const nextIndex = getDerivationPathNextIndex(
      existingGroupAccounts[existingGroupAccounts.length - 1].derivationPath,
      derivationPathTemplate
    );
    const nextDerivationPath = makeDerivationPath2(
      derivationPathTemplate,
      nextIndex,
      derivationPath
    );
    const nextCurve = curve || existingCurve;
    const { pk, pkh } = await derivePublicKeyPair2(seedphrase, nextDerivationPath, nextCurve);
    const uniqueLabel = getNextAvailableAccountLabels(label, 1)[0];
    const account = {
      type: "mnemonic",
      curve: nextCurve,
      pk,
      address: { type: "implicit", pkh },
      derivationPath: nextDerivationPath,
      seedFingerPrint: fingerPrint,
      derivationPathTemplate,
      label: uniqueLabel,
      isVerified: true,
      id: nanoid4(),
      chains: {
        [ChainFullIds5.TEZOS]: {
          address: { type: "implicit", pkh },
          derivationPath: nextDerivationPath,
          derivationPathTemplate,
          curve: nextCurve,
          pk,
          decimals: 6
        }
      }
    };
    dispatch(accountsActions.addAccount(account));
    return account;
  };
};
var useRestoreFromSecretKey = () => {
  const dispatch = useAppDispatch();
  return (secretKey, password, label, encrypted_dek, userId) => dispatch(
    restoreFromSecretKey({
      secretKey,
      password,
      label,
      encrypted_dek,
      userId
    })
  );
};
var useRestoreLedger = () => {
  const dispatch = useAppDispatch();
  return (account) => dispatch(addAccount(account));
};
var getSocialAccount = ({
  pk,
  pkh,
  label,
  idp
}) => ({
  type: "social",
  pk,
  address: { type: "implicit", pkh },
  idp,
  label
});
var useRemoveMnemonic = () => {
  const dispatch = useAppDispatch();
  const getAccountsByFingerPrint = useGetAccountsByFingerPrint();
  const removeDependenciesAndMultisigs = useRemoveDependenciesAndMultisigs();
  return (fingerPrint) => {
    removeDependenciesAndMultisigs(getAccountsByFingerPrint(fingerPrint));
    dispatch(
      removeMnemonicAndAccounts({
        fingerPrint
      })
    );
  };
};
var useRemoveNonMnemonic = () => {
  const dispatch = useAppDispatch();
  const getAccountsByType = useGetAccountsByType();
  const removeDependenciesAndMultisigs = useRemoveDependenciesAndMultisigs();
  return (accountType) => {
    removeDependenciesAndMultisigs(getAccountsByType(accountType));
    dispatch(
      removeNonMnemonicAccounts({
        accountType
      })
    );
  };
};
var useRemoveAccount = () => {
  const dispatch = useAppDispatch();
  const removeDependenciesAndMultisigs = useRemoveDependenciesAndMultisigs();
  return (account) => {
    removeDependenciesAndMultisigs([account]);
    dispatch(accountsActions.removeAccount(account));
  };
};

// src/hooks/staking.ts
import { BigNumber as BigNumber2 } from "bignumber.js";
var useGetAccountStakedBalance = (pkh) => useGetAccountState()(pkh)?.stakedBalance ?? 0;
var useGetAccountUnstakeRequests = (pkh) => useGetAccountState()(pkh)?.unstakeRequests || [];
var useGetFirstFinalizableCycle = () => {
  const { maxSlashingPeriod, consensusRightsDelay } = useGetProtocolSettings();
  return (requestedOnCycle) => requestedOnCycle + maxSlashingPeriod + consensusRightsDelay;
};
var useGetAccountBalanceDetails = (pkh) => {
  const spendableBalance = BigNumber2(useGetAccountBalance()(pkh) ?? 0);
  const stakedBalance = useGetAccountStakedBalance(pkh);
  const totalFinalizableAmount = useAccountTotalFinalizableUnstakeAmount(pkh);
  const totalPendingAmount = useAccountTotalPendingUnstakeAmount(pkh);
  const totalBalance = spendableBalance.plus(stakedBalance).plus(totalPendingAmount).plus(totalFinalizableAmount);
  return {
    spendableBalance,
    stakedBalance,
    totalFinalizableAmount,
    totalPendingAmount,
    totalBalance
  };
};
var useAccountPendingUnstakeRequests = (pkh) => useGetAccountUnstakeRequests(pkh).filter((req) => req.status === "pending");
var useAccountTotalPendingUnstakeAmount = (pkh) => {
  const unstakeRequests = useGetAccountUnstakeRequests(pkh);
  return unstakeRequests.filter((req) => req.status === "pending").reduce((acc, req) => acc.plus(req.amount), BigNumber2(0));
};
var useAccountTotalFinalizableUnstakeAmount = (pkh) => {
  const unstakeRequests = useGetAccountUnstakeRequests(pkh);
  return unstakeRequests.filter((req) => req.status === "finalizable").reduce((acc, req) => acc.plus(req.amount), BigNumber2(0));
};

// src/hooks/WalletConnect.ts
import { WalletConnectError, WcErrorCode } from "@umami/utils";
import { useDispatch as useDispatch7 } from "react-redux";

// src/walletConnect/WalletKit.ts
import { WalletKit } from "@reown/walletkit";
import { Core } from "@walletconnect/core";
var walletKit;
var createWalletKit = async () => {
  const core = new Core({
    projectId: "27cab1157f006c2d45f5c10fc2c7c4d4"
  });
  walletKit = await WalletKit.init({
    core,
    metadata: {
      name: "Umami Wallet",
      description: "Umami Wallet with WalletConnect",
      url: "https://umamiwallet.com",
      icons: ["https://umamiwallet.com/assets/favicon-32-45gq0g6M.png"]
    }
  });
  try {
    void await walletKit.engine.signClient.core.crypto.getClientId();
  } catch (error) {
    console.error("Failed to set WalletConnect clientId in localStorage: ", error);
  }
};

// src/hooks/WalletConnect.ts
var useGetWcPeerListToggle = () => {
  const wcData = useAppSelector((s) => s.walletconnect);
  return wcData.peerListUpdatedToggle;
};
var useToggleWcPeerListUpdated = () => {
  const dispatch = useDispatch7();
  return () => dispatch(wcActions.togglePeerListUpdated());
};
var useDisconnectWalletConnectPeer = () => {
  const togglePeerListUpdated = useToggleWcPeerListUpdated();
  return async (params) => {
    await walletKit.disconnectSession(params).then(() => togglePeerListUpdated());
    console.log("WC session deleted on user request", params);
  };
};
var WcScenarioType = /* @__PURE__ */ ((WcScenarioType2) => {
  WcScenarioType2[WcScenarioType2["APPROVE"] = 0] = "APPROVE";
  WcScenarioType2[WcScenarioType2["REJECT"] = 1] = "REJECT";
  return WcScenarioType2;
})(WcScenarioType || {});
var useValidateWcRequest = () => (type, id, scenario, closeModalCallback) => {
  let isValid = false;
  if (type === "session proposal") {
    const records = walletKit.getPendingSessionProposals();
    isValid = Object.values(records).some((request) => request.id === id);
  } else {
    walletKit.getPendingSessionRequests();
    const records = walletKit.getPendingSessionRequests();
    isValid = records.some((request) => request.id === id);
  }
  if (!isValid) {
    closeModalCallback();
    if (scenario === 0 /* APPROVE */) {
      console.warn(
        `This ${type} is not valid anymore. Check the connection on the dApp side and retry.`
      );
      throw new WalletConnectError(
        `This ${type} is not valid anymore. Check the connection on the dApp side and retry.`,
        WcErrorCode.DAPP_NOTIFICATION_FAILED,
        null
      );
    }
    return false;
  }
  return true;
};

// src/hooks/session.ts
import {
  getPublicKeyPairFromSk as getPublicKeyPairFromSk2,
  withTimeout
} from "@umami/chains";
import { decrypt as decrypt4 } from "@umami/crypto";
import { forIDP as forIDP2 } from "@umami/social-auth";
import { CustomError as CustomError8 } from "@umami/utils";
var SESSION_TIMEOUT = 30 * 60 * 1e3;
var LOGIN_TIMEOUT = 1e3 * 60;
var useHandleSession = () => {
  const isOnboarded = () => !!localStorage.getItem("user_requirements_nonce");
  const isSessionActive = !!useCurrentAccount();
  const setupSessionTimeout = () => {
    try {
      const timeoutId = setTimeout(() => {
        sessionStorage.clear();
      }, SESSION_TIMEOUT);
      sessionStorage.setItem("sessionTimeoutId", timeoutId.toString());
    } catch (error) {
      console.error("Failed to setup session timeout:", error);
    }
  };
  return { setupSessionTimeout, isSessionActive, isOnboarded };
};
var clearSessionKey = () => {
  localStorage.removeItem("user_requirements_nonce");
  window.location.reload();
};
var useLoginWithPassword = (defaultAccount, setupPersistence) => {
  const { isLoading, handleAsyncActionUnsafe } = useAsyncActionHandler();
  const login = (accounts, data) => handleAsyncActionUnsafe(async () => {
    let secret;
    if (defaultAccount?.type === "mnemonic") {
      secret = accounts.seedPhrases[defaultAccount.seedFingerPrint];
    } else if (defaultAccount?.type === "secret_key") {
      secret = accounts.secretKeys[defaultAccount.address.pkh];
    }
    if (secret) {
      const result = await decrypt4(secret, data.password);
      await setupPersistence(result);
    }
  });
  return { isLoading, login };
};
var useLoginWithSocial = (defaultAccount, setupPersistence) => {
  const { isLoading, handleAsyncAction } = useAsyncActionHandler();
  const login = (idp) => handleAsyncAction(
    async () => {
      const { secretKey } = await withTimeout(() => forIDP2(idp).getCredentials(), LOGIN_TIMEOUT);
      const publicKey = await getPublicKeyPairFromSk2(secretKey);
      if (publicKey.pkh !== defaultAccount?.address.pkh) {
        throw new CustomError8(
          "The selected social account doesn't match your default wallet account which was used in this backup. Please make sure you're using the same account."
        );
      }
      await setupPersistence(secretKey);
    },
    { title: "Social login failed" }
  );
  return { isLoading, login };
};
var useLoginToWallet = (defaultAccount, setupPersistence) => {
  const { isLoading: isLoadingWithPassword, login: loginWithPassword } = useLoginWithPassword(
    defaultAccount,
    setupPersistence
  );
  const { isLoading: isLoadingWithSocial, login: loginWithSocial } = useLoginWithSocial(
    defaultAccount,
    setupPersistence
  );
  const isSocialAccount = defaultAccount?.type === "social";
  return {
    isLoading: isSocialAccount ? isLoadingWithSocial : isLoadingWithPassword,
    handleLogin: () => isSocialAccount ? () => loginWithSocial(defaultAccount.idp) : loginWithPassword
  };
};
var clearStorage = () => {
  localStorage.removeItem("persist:accounts");
  localStorage.removeItem("persist:root");
  localStorage.removeItem("user_requirements_nonce");
};
var clearStorageWithReload = () => {
  clearStorage();
  location.reload();
};

// src/utils/localEncryptionKey.ts
import { decrypt as decrypt5, encrypt as encrypt5 } from "@umami/crypto";
var NONCE_NAME = "user_requirements_nonce";
var getOrCreateUserNonce = async (password) => {
  const nonce = getUserNonce();
  if (nonce !== "") {
    return nonce;
  }
  if (password) {
    const decryptedNonce = await decryptUserNonce(password);
    if (decryptedNonce) {
      return decryptedNonce;
    }
    return await createUserNonce(password);
  }
  return null;
};
var getUserNonce = () => sessionStorage.getItem(NONCE_NAME) ?? "";
var decryptUserNonce = async (password) => {
  const encryptedNonceStr = localStorage.getItem(NONCE_NAME);
  if (!encryptedNonceStr) {
    return null;
  }
  try {
    const encryptedNonce = JSON.parse(encryptedNonceStr);
    const nonce = await decrypt5(encryptedNonce, password);
    if (!nonce) {
      throw new Error("Failed to decrypt nonce");
    }
    sessionStorage.setItem(NONCE_NAME, nonce);
    return nonce;
  } catch {
    return null;
  }
};
var createUserNonce = async (password) => {
  const randomBytes = crypto.getRandomValues(new Uint8Array(32));
  const nonce = Array.from(randomBytes).map((b) => b.toString(16).padStart(2, "0")).join("");
  const encrypted = await encrypt5(nonce, password);
  localStorage.setItem(NONCE_NAME, JSON.stringify(encrypted));
  sessionStorage.setItem(NONCE_NAME, nonce);
  return nonce;
};

// src/services/multichain/useCreateMnemonicWallets.ts
import {
  ChainFullIds as ChainFullIds6,
  ChainIds as ChainIds2,
  ChainNamespaces as ChainNamespaces3,
  TEZ_DECIMALS,
  btcDerivationPathTemplates,
  defaultDerivationPathTemplates,
  getChainData,
  getChainFullIdFromNetwork as getChainFullIdFromNetwork3,
  makeDerivationPath as makeDerivationPath3,
  useChainRegistry
} from "@umami/chains";

// src/services/multichain/useSetupSaplingAccount.ts
import { generateKeys } from "@umami/chains";
import { CustomError as CustomError10 } from "@umami/utils";

// src/services/multichain/secretKeyToMnemonic.ts
import { b58DecodeAndCheckPrefix } from "@taquito/utils";
import { isValidEvmPrivateKey, normalizeEvmKey } from "@umami/chains";
import { CustomError as CustomError9 } from "@umami/utils";
import * as bip39 from "bip39";
import bs58check from "bs58check";
var entropyToMnemonic2 = (entropy) => bip39.entropyToMnemonic(Buffer.from(entropy));
var getEntropyFromEdsk = (edskString) => {
  const decoded = bs58check.decode(edskString);
  const prefixLength = 4;
  const rawBytes = decoded.slice(prefixLength);
  if (rawBytes.length === 64) {
    return rawBytes.slice(0, 32);
  }
  return rawBytes;
};
function getMnemonicFromSecretKey(sk) {
  let entropy;
  if (sk.startsWith("spsk") || sk.startsWith("p2sk")) {
    [entropy] = b58DecodeAndCheckPrefix(sk);
  } else if (sk.startsWith("edsk")) {
    entropy = getEntropyFromEdsk(sk);
  } else if (isValidEvmPrivateKey(sk)) {
    const convertedTezosKey = normalizeEvmKey(sk);
    entropy = getEntropyFromEdsk(convertedTezosKey.tezos);
  } else {
    throw new CustomError9("Secret key not supported for Shielded Transactions");
  }
  const mnemonic = entropyToMnemonic2(entropy);
  return mnemonic;
}

// src/services/multichain/useSetupSaplingAccount.ts
var useSetupSaplingAccount = () => {
  const dispatch = useAppDispatch();
  return async (params) => {
    let mnemonicToUse;
    if ("secretKey" in params) {
      mnemonicToUse = getMnemonicFromSecretKey(params.secretKey);
    } else if ("mnemonic" in params) {
      mnemonicToUse = params.mnemonic;
    } else {
      throw new CustomError10("Invalid parameters for setup sapling account");
    }
    const { viewingKey, viewingKeyAddress } = await generateKeys(mnemonicToUse);
    dispatch(
      saplingActions.setAccount({
        id: params.accountPkh,
        data: {
          viewingKey: viewingKey.toString("hex"),
          viewingKeyAddress,
          balance: "0"
        }
      })
    );
  };
};

// src/services/multichain/useCreateMnemonicWallets.ts
var useCreateMnemonicWallets = () => {
  const { registry } = useChainRegistry();
  const { networks: networks2 } = useAvailableMultichainNetworks();
  const restoreFromMnemonic = useRestoreFromMnemonic();
  const dispatch = useAppDispatch();
  const setupSaplingAccount = useSetupSaplingAccount();
  return async ({
    mnemonic,
    derivationPaths,
    derivationPathsTemplates,
    curve,
    label,
    password,
    isVerified,
    userId,
    encrypted_dek
  }) => {
    const { accounts, encryptedMnemonic } = await restoreFromMnemonic({
      mnemonic,
      password,
      derivationPath: derivationPaths?.[ChainIds2.TEZOS],
      derivationPathTemplate: defaultDerivationPathTemplates[ChainIds2.TEZOS],
      label,
      curve,
      isVerified,
      encrypted_dek,
      userId
    });
    const mapDerivationPathsToNetworks = (derivationPaths2) => networks2.reduce((acc, network) => {
      const derivationPath = derivationPaths2[network.chain];
      const chainFullId = getChainFullIdFromNetwork3(network);
      if (derivationPath) {
        const template = network.namespace === ChainNamespaces3.BITCOIN ? btcDerivationPathTemplates[network.identifier] ?? derivationPath : derivationPath;
        acc[chainFullId] = makeDerivationPath3(template);
      }
      return acc;
    }, {});
    const multichainDerivationPaths = mapDerivationPathsToNetworks(
      derivationPaths ?? derivationPathsTemplates
    );
    const multiChainData = await registry.createWallets({
      mnemonic,
      derivationPaths: multichainDerivationPaths,
      curve
    });
    const updatedAccounts = accounts.map((account) => {
      const accountData = {
        chainId: ChainFullIds6.TEZOS,
        derivationPath: account.derivationPath,
        derivationPathTemplate: account.derivationPathTemplate,
        curve: account.curve,
        address: account.address,
        pk: account.pk,
        decimals: TEZ_DECIMALS,
        userId,
        encrypted_dek
      };
      account.derivationPaths = multichainDerivationPaths;
      account.chains = getChainData([accountData, ...multiChainData]);
      return account;
    });
    dispatch(
      accountsActions.addMnemonicAccounts({
        seedFingerprint: accounts[0].seedFingerPrint,
        accounts,
        encryptedMnemonic
      })
    );
    dispatch(accountsActions.setDefaultAccount());
    for (const account of updatedAccounts) {
      if (account.chains?.[ChainFullIds6.TEZOS]) {
        await setupSaplingAccount({
          mnemonic,
          accountPkh: account.address.pkh
        });
      }
    }
    return updatedAccounts;
  };
};

// src/services/multichain/useGetWallets.ts
var useGetWallets = (account) => Object.entries(account?.chains ?? {}).map(([chainId, chain]) => ({
  address: chain?.address ?? { pkh: "" },
  chainId,
  pk: chain?.pk ?? ""
}));

// src/services/multichain/useGetTokensAndBalances.ts
import { getTokenPrice, getTokenSymbol as getTokenSymbol3 } from "@umami/chains";
import { useEffect as useEffect2, useMemo as useMemo2 } from "react";

// src/services/multichain/useGetAllBalances.ts
import { useQuery as useQuery2 } from "@tanstack/react-query";
import {
  ChainIds as ChainIds3,
  CryptoSymbols,
  getChainFullIdFromNetwork as getChainFullIdFromNetwork4,
  mutezToTez as mutezToTez2,
  useChainRegistry as useChainRegistry2
} from "@umami/chains";
import { isMultichainEnabled } from "@umami/utils";

// src/services/multichain/utils.ts
import { useCustomToast as useCustomToast3 } from "@umami/utils";
var useProcessFulfilledData = () => {
  const toast = useCustomToast3();
  return (data) => {
    if (data.errors.length > 0) {
      data.errors.forEach((error) => {
        if (!toast.isActive(error.message)) {
          toast({
            description: error.message,
            id: error.message,
            status: "error",
            isClosable: true
          });
        }
      });
    }
    return data.fulfilled;
  };
};

// src/services/multichain/useGetAllBalances.ts
var useGetAllBalances = () => {
  const { registry } = useChainRegistry2();
  const currentAccount = useCurrentAccount();
  const network = useSelectedNetwork();
  const processFulfilledData = useProcessFulfilledData();
  const { totalBalance: tezosMutezBalance } = useGetAccountBalanceDetails(
    currentAccount.address.pkh
  );
  const tezosTezBalance = mutezToTez2(tezosMutezBalance.toString()).toFixed();
  const tezosBalance = {
    chainId: getChainFullIdFromNetwork4(network),
    balance: tezosTezBalance,
    symbol: CryptoSymbols[ChainIds3.TEZOS]
  };
  const { data: multiChainBalances = [], isLoading } = useQuery2({
    queryKey: ["getAllBalances", currentAccount],
    queryFn: () => registry.getAllBalances(),
    select: processFulfilledData,
    retry: 3,
    enabled: isMultichainEnabled()
  });
  const formattedMultiChainBalances = multiChainBalances.map((balance) => ({
    chainId: balance.chainId,
    balance: balance.total,
    symbol: balance.symbol
  }));
  const balances = [tezosBalance, ...formattedMultiChainBalances];
  const symbols = balances.map((balance) => balance.symbol);
  const hasAssets = isMultichainEnabled() && balances.some((balance) => Number(balance.balance) > 0);
  return { balances, isLoading, hasAssets, symbols };
};

// src/services/multichain/useGetAllTokens.ts
import { useQuery as useQuery3 } from "@tanstack/react-query";
import { BLOCK_TIME, useChainRegistry as useChainRegistry3 } from "@umami/chains";
import { useSelectedNetwork as useSelectedNetwork2 } from "@umami/state";
import { isMultichainEnabled as isMultichainEnabled2 } from "@umami/utils";
var getTokenSymbol2 = (token) => "symbol" in token ? token.symbol : token.metadata?.symbol;
var useGetAllTokens = () => {
  const getTezosTokens = useGetAccountAllTokens();
  const currentAccount = useCurrentAccount();
  const network = useSelectedNetwork2();
  const { registry } = useChainRegistry3();
  const processFulfilledData = useProcessFulfilledData();
  const tezosTokens = getTezosTokens(currentAccount.address.pkh);
  const { data: multichainTokens = [], isLoading: isMultichainTokensLoading } = useQuery3({
    queryKey: ["getAllTokensWithPrices", currentAccount],
    queryFn: () => registry.getAllTokens(),
    enabled: isMultichainEnabled2(),
    retry: 3,
    select: processFulfilledData,
    refetchInterval: network.blockTime ?? BLOCK_TIME,
    refetchOnMount: false
  });
  const mergedTokens = [...tezosTokens, ...multichainTokens];
  const tokenSymbols = mergedTokens.map(getTokenSymbol2).filter((symbol) => Boolean(symbol));
  return {
    tokens: mergedTokens,
    isLoading: isMultichainTokensLoading,
    symbols: tokenSymbols
  };
};

// src/services/multichain/useGetAssetsPrices.ts
import { useQuery as useQuery4 } from "@tanstack/react-query";
import { fetchPrices } from "@umami/chains";
import { isMultichainEnabled as isMultichainEnabled3 } from "@umami/utils";
var PRICE_REFETCH_INTERVAL = 1e3 * 60;
var useGetAssetsPrices = (symbols, enabled = true) => {
  const { data: prices = {}, isLoading } = useQuery4({
    queryKey: ["getAssetsPrices", symbols],
    queryFn: () => fetchPrices(symbols),
    refetchInterval: (query) => query.state.status === "success" ? PRICE_REFETCH_INTERVAL : false,
    refetchOnMount: false,
    retry: 3,
    enabled: isMultichainEnabled3() && enabled
  });
  return { prices, isLoading };
};

// src/services/multichain/useGetSaplingAccount.ts
import { RpcClient } from "@taquito/rpc";
import { InMemoryViewingKey, SaplingTransactionViewer } from "@taquito/sapling";
import { RpcReadAdapter } from "@taquito/taquito";
import { useCallback as useCallback2, useMemo } from "react";
var useGetSaplingAccount = (accountPkh) => useAppSelector((state) => state.sapling[accountPkh]);
var useGetSaplingViewingKey = (accountPkh) => useAppSelector((state) => state.sapling[accountPkh]?.viewingKey);
var useGetSaplingViewingKeyAddress = (accountPkh) => useAppSelector((state) => state.sapling[accountPkh]?.viewingKeyAddress);
var useGetTzAddressBySaplingAddress = (saplingAddress) => useAppSelector((state) => {
  const entry = Object.entries(state.sapling).find(
    ([_tzAddress, data]) => data?.viewingKeyAddress === saplingAddress
  );
  return entry?.[0];
});
var useGetCurrentSaplingViewingKey = () => {
  const currentAccount = useCurrentAccount();
  return useGetSaplingViewingKey(currentAccount?.address.pkh ?? "");
};
var useGetCurrentSaplingViewingKeyAddress = () => {
  const currentAccount = useCurrentAccount();
  return useGetSaplingViewingKeyAddress(currentAccount?.address.pkh ?? "");
};
var useGetCurrentSaplingTransactions = () => {
  const { saplingContractAddress, rpcUrl } = useSelectedNetwork();
  const viewingKey = useGetCurrentSaplingViewingKey();
  const viewer = useMemo(() => {
    if (!viewingKey || !saplingContractAddress) {
      return null;
    }
    const viewingKeyProvider = new InMemoryViewingKey(viewingKey);
    return new SaplingTransactionViewer(
      viewingKeyProvider,
      { contractAddress: saplingContractAddress },
      new RpcReadAdapter(new RpcClient(rpcUrl))
    );
  }, [viewingKey, saplingContractAddress, rpcUrl]);
  return useCallback2(async () => {
    if (!viewer) {
      return [];
    }
    return viewer.getTransactionsWithoutChangeRaw();
  }, [viewer]);
};

// src/services/multichain/useGetTokensAndBalances.ts
var useGetTokensAndBalances = () => {
  const dispatch = useAppDispatch();
  const {
    balances,
    hasAssets: hasBalancesAssets,
    isLoading: isBalancesLoading,
    symbols: balanceSymbols
  } = useGetAllBalances();
  const { tokens, isLoading: isTokensLoading, symbols: tokenSymbols } = useGetAllTokens();
  const mergedSymbols = [.../* @__PURE__ */ new Set([...balanceSymbols, ...tokenSymbols])];
  const { prices, isLoading: isPricesLoading } = useGetAssetsPrices(
    mergedSymbols,
    mergedSymbols.length > 0 && !isTokensLoading && !isBalancesLoading
  );
  const normalizedPrices = useMemo2(() => {
    if (!Object.keys(prices).length) {
      return {};
    }
    const normalizedPrices2 = {};
    tokens.forEach((token) => {
      const symbol = getTokenSymbol3(token);
      if (symbol && !(symbol in normalizedPrices2)) {
        normalizedPrices2[symbol] = getTokenPrice(token, prices);
      }
    });
    balances.forEach((balance) => {
      if (balance.symbol && !(balance.symbol in normalizedPrices2)) {
        const priceData = prices[balance.symbol];
        normalizedPrices2[balance.symbol] = priceData[0]?.quote?.USD?.price ?? 0;
      }
    });
    return normalizedPrices2;
  }, [prices, tokens, balances]);
  const tokenWithPrices = useMemo2(() => {
    if (!tokens.length || !tokenSymbols.length || !Object.keys(normalizedPrices).length) {
      return tokens.map((token) => ({
        ...token,
        usdPrice: 0,
        totalUsdPrice: 0
      }));
    }
    return tokens.map((token) => {
      const symbol = getTokenSymbol3(token);
      const usdPrice = symbol ? normalizedPrices[symbol] ?? 0 : 0;
      return {
        ...token,
        usdPrice,
        totalUsdPrice: usdPrice ? Number(token.balance) * usdPrice : 0
      };
    });
  }, [tokens, tokenSymbols, normalizedPrices]);
  const balancesWithPrices = useMemo2(() => {
    if (!balances.length || !balanceSymbols.length || !Object.keys(normalizedPrices).length) {
      return balances.map((balance) => ({
        ...balance,
        usdPrice: 0,
        totalUsdPrice: 0
      }));
    }
    return balances.map((balance) => {
      const usdPrice = normalizedPrices[balance.symbol] ?? 0;
      const totalUsdPrice = usdPrice ? Number(balance.balance) * usdPrice : 0;
      return {
        ...balance,
        usdPrice,
        totalUsdPrice
      };
    });
  }, [balances, balanceSymbols, normalizedPrices]);
  useEffect2(() => {
    if (Object.keys(normalizedPrices).length > 0) {
      dispatch(accountsActions.setPrices(normalizedPrices));
    }
  }, [normalizedPrices, dispatch]);
  const currentAccount = useCurrentAccount();
  const saplingAccount = useGetSaplingAccount(currentAccount?.address.pkh ?? "");
  const hasShieldedBalance = Number(saplingAccount?.balance ?? 0) > 0;
  const isLoading = isBalancesLoading || isTokensLoading || isPricesLoading;
  const hasAssets = Boolean(hasBalancesAssets || tokens.length || hasShieldedBalance);
  const getTokenUsdPrice = (symbol, amount) => {
    const numericAmount = typeof amount === "string" ? Number(amount) : amount;
    if (!Object.keys(prices).length || !symbol || numericAmount <= 0) {
      return 0;
    }
    const usdPrice = symbol in prices ? prices[symbol][0].quote.USD.price ?? 0 : 0;
    return usdPrice * numericAmount;
  };
  return {
    balances: balancesWithPrices,
    hasAssets,
    isLoading,
    tokens: tokenWithPrices,
    getTokenUsdPrice
  };
};

// src/services/multichain/useGetTotalBalance.ts
var useGetTotalBalance = () => {
  const { balances, tokens, isLoading, hasAssets } = useGetTokensAndBalances();
  const coinsTotalBalance = balances.reduce((acc, balance2) => acc + balance2.totalUsdPrice, 0);
  const tokensTotalBalance = tokens.reduce((acc, token) => acc + token.totalUsdPrice, 0);
  const balance = coinsTotalBalance + tokensTotalBalance;
  return { balance, isLoading, hasAssets };
};

// src/services/multichain/useGetChainBalance.ts
import { ChainFullIds as ChainFullIds7, ChainIds as ChainIds4, CryptoSymbols as CryptoSymbols2 } from "@umami/chains";
var emptyBalance = {
  chainId: ChainFullIds7.TEZOS,
  balance: "0",
  totalUsdPrice: 0,
  symbol: CryptoSymbols2[ChainIds4.TEZOS]
};
var useGetChainBalance = () => {
  const { balances } = useGetAllBalances();
  return (chainId) => {
    const balance = balances.find((b) => b.chainId === chainId) ?? emptyBalance;
    return balance;
  };
};

// src/services/multichain/useCreateSocialWallets.ts
import {
  ChainFullIds as ChainFullIds8,
  TEZ_DECIMALS as TEZ_DECIMALS2,
  getChainData as getChainData2,
  getPublicKeyPairFromSk as getPublicKeyPairFromSk3,
  useChainRegistry as useChainRegistry4
} from "@umami/chains";
var useCreateSocialWallets = () => {
  const { registry } = useChainRegistry4();
  const dispatch = useAppDispatch();
  const setupSaplingAccount = useSetupSaplingAccount();
  return async ({ secretKey, email, name, id, idp }) => {
    try {
      const { pk, pkh } = await getPublicKeyPairFromSk3(secretKey);
      const account = getSocialAccount({ pk, pkh, label: email || name || id, idp });
      const multiChainData = await registry.createWallets({
        secretKey
      });
      const accountData = {
        chainId: ChainFullIds8.TEZOS,
        address: account.address,
        pk: account.pk,
        decimals: TEZ_DECIMALS2
      };
      account.id = id;
      account.chains = getChainData2([accountData, ...multiChainData]);
      if (account.chains[ChainFullIds8.TEZOS]) {
        await setupSaplingAccount({
          secretKey,
          accountPkh: account.address.pkh
        });
      }
      dispatch(accountsActions.addAccount(account));
      dispatch(accountsActions.setDefaultAccount());
      return account;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };
};

// src/services/multichain/useGetAllNfts.ts
import { useQuery as useQuery5 } from "@tanstack/react-query";
import { BLOCK_TIME as BLOCK_TIME2, useChainRegistry as useChainRegistry5 } from "@umami/chains";
import { useSelectedNetwork as useSelectedNetwork3 } from "@umami/state";
import { isMultichainEnabled as isMultichainEnabled4 } from "@umami/utils";
var useGetAllNfts = () => {
  const getTezosNfts = useGetAccountNFTs();
  const currentAccount = useCurrentAccount();
  const network = useSelectedNetwork3();
  const { registry } = useChainRegistry5();
  const processFulfilledData = useProcessFulfilledData();
  const { data: multichainNftBalances = [] } = useQuery5({
    queryKey: ["getAllNfts", currentAccount],
    queryFn: () => registry.getAllNfts(),
    enabled: isMultichainEnabled4(),
    select: processFulfilledData,
    retry: 3,
    refetchInterval: network.blockTime ?? BLOCK_TIME2,
    refetchOnMount: false
  });
  return (pkh) => {
    const tezosNfts = getTezosNfts(pkh);
    return [...tezosNfts, ...multichainNftBalances];
  };
};

// src/services/multichain/useCreateSecretKeyWallets.ts
import {
  ChainFullIds as ChainFullIds9,
  TEZ_DECIMALS as TEZ_DECIMALS3,
  getChainData as getChainData3,
  normalizeSecretKey,
  useChainRegistry as useChainRegistry6
} from "@umami/chains";
var useCreateSecretKeyWallets = () => {
  const { registry } = useChainRegistry6();
  const dispatch = useAppDispatch();
  const setupSaplingAccount = useSetupSaplingAccount();
  return async ({
    secretKey,
    label,
    password,
    encrypted_dek,
    userId
  }) => {
    const { tezos: tezosKey } = await normalizeSecretKey(secretKey);
    const { account, encryptedSecretKey } = await makeSecretKeyAccount({
      secretKey: tezosKey,
      label,
      password,
      encrypted_dek,
      userId
    });
    const accountData = {
      chainId: ChainFullIds9.TEZOS,
      address: account.address,
      pk: account.pk,
      curve: account.curve,
      derivationPath: void 0,
      derivationPathTemplate: void 0,
      decimals: TEZ_DECIMALS3,
      userId,
      encrypted_dek
    };
    const multichainData = await registry.createWallets({
      secretKey
    });
    const secretKeyAccount = account;
    secretKeyAccount.chains = getChainData3([accountData, ...multichainData]);
    if (secretKeyAccount.chains[ChainFullIds9.TEZOS]) {
      await setupSaplingAccount({
        secretKey,
        accountPkh: secretKeyAccount.address.pkh
      });
    }
    dispatch(accountsActions.addAccount(secretKeyAccount));
    dispatch(accountsActions.setDefaultAccount());
    dispatch(
      accountsActions.addSecretKey({ pkh: secretKeyAccount.address.pkh, encryptedSecretKey })
    );
    return secretKeyAccount;
  };
};

// src/services/multichain/useCreateLedgerWallets.ts
import { nanoid as nanoid5 } from "@reduxjs/toolkit";
import {
  ChainFullIds as ChainFullIds10,
  ChainNamespaces as ChainNamespaces4,
  NETWORK_CONFIGS as NETWORK_CONFIGS3,
  getChainData as getChainData4,
  getLedgerPublicKeyPair,
  makeDerivationPath as makeDerivationPath4,
  parseChainAddress,
  parseChainFullId as parseChainFullId3,
  useChainRegistry as useChainRegistry7
} from "@umami/chains";
var useCreateLedgerWallets = () => {
  const getNextAvailableAccountLabels = useGetNextAvailableAccountLabels();
  const restoreLedger = useRestoreLedger();
  const { registry } = useChainRegistry7();
  const dispatch = useAppDispatch();
  return async ({
    derivationPathTemplate,
    curve,
    chainId = ChainFullIds10.TEZOS
  }) => {
    const { namespace } = parseChainFullId3(chainId);
    const label = getNextAvailableAccountLabels("Account")[0];
    const derivationPath = makeDerivationPath4(derivationPathTemplate, 0);
    let pk;
    let pkh;
    if (namespace === ChainNamespaces4.TEZOS) {
      ({ pk, pkh } = await getLedgerPublicKeyPair(derivationPath, curve));
    } else {
      const signer = await registry.getLedgerSigner(chainId, derivationPath);
      ({ publicKey: pk, address: pkh } = await signer.getPublicKeyPair());
      await signer.close();
    }
    const address = parseChainAddress(pkh, chainId);
    const preparedAddress = namespace === ChainNamespaces4.TEZOS ? address.pkh : `${address.pkh}@${chainId}`;
    const account = {
      type: "ledger",
      derivationPathTemplate,
      derivationPath,
      pk,
      address: {
        type: address.type,
        pkh: preparedAddress
      },
      label,
      curve,
      id: nanoid5()
    };
    const accountData = {
      chainId,
      address,
      pk: account.pk,
      curve: account.curve,
      derivationPath: account.derivationPath,
      derivationPathTemplate: account.derivationPathTemplate,
      decimals: NETWORK_CONFIGS3[chainId].decimals
    };
    account.chains = getChainData4([accountData]);
    restoreLedger(account);
    dispatch(accountsActions.setCurrent(account.address.pkh));
    return account;
  };
};

// src/services/multichain/useIsChainEnabled.ts
import { ChainIds as ChainIds5, getChainFullIdFromNetwork as getChainFullIdFromNetwork5 } from "@umami/chains";
var useIsChainEnabled = () => {
  const currentNetwork = useSelectedNetwork();
  const currentAccount = useCurrentAccount();
  return (network) => {
    const chainId = getChainFullIdFromNetwork5(network);
    if (network.chain === ChainIds5.TEZOS) {
      return currentNetwork.name === network.name;
    }
    return !!currentAccount?.chains?.[chainId];
  };
};

// src/services/multichain/useEnableChain.ts
import { useChainRegistry as useChainRegistry8 } from "@umami/chains";
var useEnableChain = () => {
  const { registry } = useChainRegistry8();
  const { configs } = useAppSelector((s) => s.networks);
  const currentAccount = useCurrentAccount();
  const dispatch = useAppDispatch();
  return async (chainId, options) => {
    const chainData = await registry.addChain(chainId, configs[chainId], options);
    dispatch(
      accountsActions.addAccountChain({ accountId: currentAccount.id ?? "", chainId, chainData })
    );
  };
};

// src/services/multichain/useDisableChain.ts
import { useChainRegistry as useChainRegistry9 } from "@umami/chains";
var useDisableChain = () => {
  const { registry } = useChainRegistry9();
  const currentAccount = useCurrentAccount();
  const dispatch = useAppDispatch();
  return (chainId) => {
    registry.removeChain(chainId);
    dispatch(accountsActions.removeAccountChain({ accountId: currentAccount.id ?? "", chainId }));
  };
};

// src/services/multichain/useSetupSaplingAccountsForExistingWallets.ts
import { decrypt as decrypt6 } from "@umami/crypto";
import { CustomError as CustomError11 } from "@umami/utils";
var processSocialAccount = async (params, setupSaplingAccount) => {
  const { getState, secretKey, accountPkh } = params;
  const saplingState = getState().sapling;
  if (saplingState[accountPkh]) {
    return;
  }
  await setupSaplingAccount({
    secretKey,
    accountPkh
  });
};
var processMnemonicAccount = async (account, password, seedPhrases, setupSaplingAccount) => {
  const encryptedMnemonic = seedPhrases[account.seedFingerPrint];
  if (!encryptedMnemonic) {
    return;
  }
  const mnemonic = await decrypt6(encryptedMnemonic, password);
  await setupSaplingAccount({
    mnemonic,
    accountPkh: account.address.pkh
  });
};
var processSecretKeyAccount = async (account, password, secretKeys, setupSaplingAccount) => {
  const encryptedSecretKey = secretKeys[account.address.pkh];
  if (!encryptedSecretKey) {
    return;
  }
  const secretKey = await decrypt6(encryptedSecretKey, password);
  await setupSaplingAccount({
    secretKey,
    accountPkh: account.address.pkh
  });
};
var processExistingWallets = async (params, setupSaplingAccount) => {
  const { getState, password } = params;
  if (!password) {
    return;
  }
  const state = getState();
  const mnemonicAndSecretKeyAccounts = state.accounts.items.filter(
    (account) => account.type === "secret_key" || account.type === "mnemonic"
  );
  const saplingState = state.sapling;
  const seedPhrases = state.accounts.seedPhrases;
  const secretKeys = state.accounts.secretKeys;
  for (const account of mnemonicAndSecretKeyAccounts) {
    const accountPkh = account.address.pkh;
    if (saplingState[accountPkh]) {
      continue;
    }
    try {
      if (account.type === "mnemonic") {
        await processMnemonicAccount(account, password, seedPhrases, setupSaplingAccount);
      } else {
        await processSecretKeyAccount(account, password, secretKeys, setupSaplingAccount);
      }
    } catch {
      throw new CustomError11(`Failed to setup sapling for the account with address: ${accountPkh}`);
    }
  }
};
var useSetupSaplingAccountsForExistingWallets = () => {
  const setupSaplingAccount = useSetupSaplingAccount();
  return async (params) => {
    if (params.accountType === "social") {
      await processSocialAccount(
        {
          secretKey: params.secretKey,
          accountPkh: params.accountPkh,
          getState: params.getState
        },
        setupSaplingAccount
      );
    } else {
      await processExistingWallets(
        {
          password: params.password,
          getState: params.getState
        },
        setupSaplingAccount
      );
    }
  };
};

// src/services/multichain/useIsSaplingEnabled.ts
import {
  ChainNamespaces as ChainNamespaces5,
  parseChainFullId as parseChainFullId4
} from "@umami/chains";
var allowedAccountTypes = ["mnemonic", "social", "secret_key"];
var allowedNamespaces = [ChainNamespaces5.TEZOS];
var useIsSaplingEnabled = () => {
  const currentAccount = useCurrentAccount();
  const selectedNetwork = useSelectedNetwork();
  const { configs } = useAppSelector((s) => s.networks);
  return (chainId) => {
    if (!currentAccount) {
      return false;
    }
    const isAccountTypeAllowed = allowedAccountTypes.includes(currentAccount.type);
    if (!chainId) {
      return isAccountTypeAllowed && !!selectedNetwork.saplingContractAddress;
    }
    const { namespace } = parseChainFullId4(chainId);
    const isNamespaceAllowed = allowedNamespaces.includes(namespace);
    const network = configs[chainId];
    const isContractAvailable = !!network.saplingContractAddress;
    return isAccountTypeAllowed && isNamespaceAllowed && isContractAvailable;
  };
};
export {
  NONCE_NAME,
  PROHIBITED_CHARACTERS,
  VERSION,
  WalletClient,
  WcScenarioType,
  accountsActions,
  accountsInitialState,
  accountsMigrations,
  accountsSlice,
  addSafeSendSecret,
  addTestAccount,
  addTestAccounts,
  announcementActions,
  announcementInitialState,
  announcementSlice,
  assetsActions,
  assetsInitialState,
  assetsSlice,
  batchesActions,
  batchesInitialState,
  batchesSlice,
  beaconActions,
  beaconInitialState,
  beaconSlice,
  changeMnemonicPassword,
  clearSessionKey,
  clearStorage,
  clearStorageWithReload,
  contactsActions,
  contactsInitialState,
  contactsSlice,
  createUserNonce,
  createWalletKit,
  decryptSeedphrasesAndSecretkeys,
  decryptUserNonce,
  encryptSeedphrasesAndSecretkeys,
  errorsActions,
  errorsInitialState,
  errorsSlice,
  estimateAndUpdateBatch,
  generate24WordMnemonic,
  getCurve,
  getMnemonicFromSecretKey,
  getOrCreateUserNonce,
  getSocialAccount,
  getUserNonce,
  hideConfidentialData,
  initializePersistence,
  isDeprecatedNetwork,
  isEncryptedSecretKeyPrefix,
  logout,
  mainStoreMigrations,
  makeSecretKeyAccount,
  makeStore,
  mockToast,
  multisigsActions,
  multisigsInitialState,
  multisigsSlice,
  networksActions,
  networksSlice,
  parsePeerInfo,
  protocolSettingsActions,
  protocolSettingsInitialState,
  protocolSettingsSlice,
  removeSafeSendSecret,
  renameAccount,
  restoreFromSecretKey,
  restoreRevealedPublicKeyPairs,
  safeSendSecretsSlice,
  saplingActions,
  saplingInitialState,
  saplingSlice,
  tokensActions,
  tokensInitialState,
  tokensSlice,
  useAccountPendingUnstakeRequests,
  useAccountTotalFinalizableUnstakeAmount,
  useAccountTotalPendingUnstakeAmount,
  useAddBeaconConnection,
  useAddPeer,
  useAddressExistsInContacts,
  useAllAccounts,
  useAllContacts,
  useAllNfts,
  useAppDispatch,
  useAppSelector,
  useAsyncActionHandler,
  useAvailableMultichainNetworks,
  useAvailableNetworks,
  useBakerList,
  useBatches,
  useBeaconPeers,
  useBuyTezUrl,
  useClearBatch,
  useContactsForSelectedNetwork,
  useCreateLedgerWallets,
  useCreateMnemonicWallets,
  useCreateSecretKeyWallets,
  useCreateSocialWallets,
  useCurrentAccount,
  useDefaultAccount,
  useDeriveMnemonicAccount,
  useDisableChain,
  useDisconnectWalletConnectPeer,
  useDownloadBackupFile,
  useEnableChain,
  useFindNetwork,
  useGetAccountAllTokens,
  useGetAccountBalance,
  useGetAccountBalanceDetails,
  useGetAccountDelegate,
  useGetAccountNFTs,
  useGetAccountStakedBalance,
  useGetAccountState,
  useGetAccountUnstakeRequests,
  useGetAccountsByFingerPrint,
  useGetAccountsByType,
  useGetAllBalances,
  useGetAllNfts,
  useGetAllTokens,
  useGetAssetPrice,
  useGetAssetsPrices,
  useGetBaker,
  useGetBeaconConnectionInfo,
  useGetBeaconPeersForAccounts,
  useGetBestSignerForAccount,
  useGetChainBalance,
  useGetContactName,
  useGetCurrentSaplingTransactions,
  useGetCurrentSaplingViewingKey,
  useGetCurrentSaplingViewingKeyAddress,
  useGetDecryptedMnemonic,
  useGetDefaultAccount,
  useGetDollarBalance,
  useGetFirstFinalizableCycle,
  useGetImplicitAccount,
  useGetImplicitAccountSafe,
  useGetMostFundedImplicitAccount,
  useGetMultichainSecretKey,
  useGetNetworkByFullId,
  useGetNetworkChain,
  useGetNetworkName,
  useGetNetworkSymbol,
  useGetNextAvailableAccountLabels,
  useGetOperationDestination,
  useGetOwnedAccount,
  useGetOwnedAccountSafe,
  useGetOwnedSignersForAccount,
  useGetPendingMultisigOperations,
  useGetProtocolSettings,
  useGetSaplingAccount,
  useGetSaplingViewingKey,
  useGetSaplingViewingKeyAddress,
  useGetSecretKey,
  useGetToken,
  useGetTokenTransfer,
  useGetTokenType,
  useGetTokensAndBalances,
  useGetTotalBalance,
  useGetTzAddressBySaplingAddress,
  useGetUserAlerts,
  useGetWallets,
  useGetWcPeerListToggle,
  useHandleSession,
  useImplicitAccounts,
  useIsBlockFinalised,
  useIsChainEnabled,
  useIsLoading,
  useIsOwnedAddress,
  useIsPasswordSet,
  useIsSaplingEnabled,
  useLastTimeUpdated,
  useLoginToWallet,
  useMultisigAccounts,
  useMutezToUsd,
  useProcessFulfilledData,
  useRemoveAccount,
  useRemoveBatchItem,
  useRemoveBeaconConnection,
  useRemoveBeaconPeer,
  useRemoveBeaconPeerBySenderId,
  useRemoveBeaconPeersByAccounts,
  useRemoveDependenciesAndMultisigs,
  useRemoveMnemonic,
  useRemoveNonMnemonic,
  useResetBeaconConnections,
  useResetState,
  useRestoreBackup,
  useRestoreFromMnemonic,
  useRestoreFromSecretKey,
  useRestoreLedger,
  useRestoreRevealedMnemonicAccounts,
  useSeedPhrases,
  useSelectNetwork,
  useSelectedNetwork,
  useSetupSaplingAccount,
  useSetupSaplingAccountsForExistingWallets,
  useSortedContacts,
  useSpendableBalanceOfAllAccounts,
  useTezToDollar,
  useToggleWcPeerListUpdated,
  useValidateMasterPassword,
  useValidateName,
  useValidateNewContactPkh,
  useValidateNewContactPkhByNamespace,
  useValidateWcRequest,
  validateBackup,
  walletKit,
  wcActions,
  wcInitialState,
  wcSlice,
  withoutDeprecatedAccountChains,
  withoutDeprecatedNetworks
};
